import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, DatePicker, theme, Table, message, Tooltip, Tag, Tabs, Select, Button, Form, Space, App, Statistic } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
} from 'recharts';
import { REPORT_API, TENANT_API, RENT_API, ELECTRICITY_API, WATER_API } from '../constants/api';
import { SearchOutlined, ReloadOutlined, DeleteOutlined, ExportOutlined } from '@ant-design/icons';
import axios from '../services/axiosConfig';
import _ from 'lodash';
import moment from 'moment';
import InvoiceDetailModal from '../components/rent/InvoiceDetailModal';

function Reports() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const resizeTimeoutRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [contractData, setContractData] = useState([]);
  const [factoryData, setFactoryData] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [contractSummary, setContractSummary] = useState(null);
  const [contractsByStatus, setContractsByStatus] = useState([]);
  const [contractsByFactory, setContractsByFactory] = useState([]);
  const [contractsByTenant, setContractsByTenant] = useState([]);
  const [utilityData, setUtilityData] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState('all');
  const [tenantReportData, setTenantReportData] = useState(null);
  const [tenantDateRange, setTenantDateRange] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10, // Tăng số lượng hiển thị trên mỗi trang
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    invoiceType: undefined,
    fromDate: undefined,
    toDate: undefined,
    tenantID: undefined
  });
  const [form] = Form.useForm();
  const [monthlyComparisonData, setMonthlyComparisonData] = useState({
    summary: {
      totalRevenue: 0,
      averageRevenue: 0,
      averageGrowth: 0,
      maxGrowth: 0
    },
    data: []
  });
  const [revenueComparisonData, setRevenueComparisonData] = useState({
    summary: {
      totalRevenue: 0,
      averageRevenue: 0,
      averageGrowth: 0,
      maxGrowth: 0
    },
    data: []
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [comparisonDateRange, setComparisonDateRange] = useState([]);
  const [factoryGroupDateRange, setFactoryGroupDateRange] = useState(null);
  const [factoryGroupData, setFactoryGroupData] = useState([]);
  const [factoryGroupLoading, setFactoryGroupLoading] = useState(false);
  const [selectedFactoryGroup, setSelectedFactoryGroup] = useState(null);
  const [factoryGroupDetail, setFactoryGroupDetail] = useState(null);
  const [factoryGroupInvoiceType, setFactoryGroupInvoiceType] = useState('all');
  const [factoryGroupInvoiceStatus, setFactoryGroupInvoiceStatus] = useState('all');
  const [detailModal, setDetailModal] = useState({ visible: false, record: null });

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      setWindowWidth(window.innerWidth);
    }, 200);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  const formatRevenue = (value) => {
    if (!value && value !== 0) return '-';
    return value.toLocaleString('vi-VN');
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: 0 }}>{`Tháng ${label}`}</p>
          <p style={{ margin: 0, color: '#1890ff' }}>
            {`Tổng doanh thu: ${payload[0].value.toLocaleString('vi-VN')} VNĐ`}
          </p>

          <p style={{ margin: 0, color: '#52c41a' }}>
            {`Tiền thuê: ${payload[0].payload.rentRevenue.toLocaleString('vi-VN')} VNĐ`}
          </p>
          {payload[1] && (
            <p style={{ margin: 0, color: '#722ed1' }}>
              {`Số hợp đồng: ${payload[1].value}`}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const columns = [
    {
      title: 'Mã hóa đơn',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
    },
    {
      title: 'Xưởng',
      dataIndex: 'factoryName',
      key: 'factoryName',
    },
    {
      title: 'Khách thuê',
      dataIndex: 'tenantName',
      key: 'tenantName',
    },
    {
      title: 'Loại hóa đơn',
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      render: (type) => {
        return type === 'rent' ? 'Tiền thuê xưởng' : type === 'electricity' ? 'Tiền điện' : type === 'water' ? 'Tiền nước sạch' : type === 'monthly' ? 'Hóa đơn tổng hợp' : 'Dịch vụ & Hạ tầng';
      }
    },
    {
      title: 'Kỳ thanh toán',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'Số tiền (VNĐ)',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => new Intl.NumberFormat('vi-VN').format(amount),
    },
    {
      title: 'Đã thanh toán (VNĐ)',
      key: 'paidAmount',
      render: (_, record) => {
        const paidAmount = record.paidAmount || 0;
        return new Intl.NumberFormat('vi-VN').format(paidAmount);
      }
    },
    {
      title: 'Còn thiếu (VNĐ)',
      key: 'remainingAmount',
      render: (_, record) => {
        const paidAmount = record.paidAmount || 0;
        const remainingAmount = record.amount - paidAmount;
        return new Intl.NumberFormat('vi-VN').format(remainingAmount);
      }
    },
    {
      title: 'Hạn thanh toán',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (dueDate) => {
        return dueDate ? dueDate.split('T')[0].split('-').reverse().join('/') : 'Chưa có';
      }
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        return <Tag style={status === 'pending' ? { backgroundColor: '#FFD700', color: '#000' } : status === 'paid' ? { backgroundColor: '#008000', color: '#fff' } : status === 'overdue' ? { backgroundColor: '#FF0000', color: '#fff' } : status === 'partial' ? { backgroundColor: '#FFA500', color: '#fff' } : ''}>{status === 'pending' ? 'Chờ thanh toán' : status === 'paid' ? 'Đã thanh toán' : status === 'overdue' ? 'Quá hạn' : status === 'partial' ? 'Chưa thanh toán đủ' : ''}</Tag>;
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => setDetailModal({ visible: true, record: { id: record.id || record.invoiceNumber } })}
        >
          Xem chi tiết
        </Button>
      ),
    }
  ];

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      let startDate, endDate;

      if (dateRange && dateRange.length === 2) {
        startDate = dateRange[0].format('YYYY-MM-DD');
        endDate = dateRange[1].format('YYYY-MM-DD');
      } else {
        const now = new Date();
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      }

      const response = await fetch(REPORT_API.REVENUE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate,
          endDate,
          groupBy: 'month',
          tenantId: selectedTenant || undefined
        }),
      });

      const result = await response.json();

      if (result.data && result.data.revenue) {
        // Chuẩn hoá data cho biểu đồ + bổ sung serviceRevenue (dịch vụ & hạ tầng)
        const formatted = (result.data.revenue || []).map(item => {
          const rentRevenue = item.rentRevenue || 0;
          const electricityRevenue = item.electricityRevenue || 0;
          const waterRevenue = item.waterRevenue || 0;
          const totalRevenue = item.totalRevenue ?? item.revenue ?? 0;
          const otherRevenue =
            item.otherRevenue ?? Math.max(0, totalRevenue - rentRevenue - electricityRevenue - waterRevenue);
          return {
            ...item,
            month: item.month ? String(item.month).padStart(2, '0') : item.month,
            revenue: totalRevenue,
            rentRevenue,
            electricityRevenue,
            waterRevenue,
            otherRevenue,
          };
        });
        setRevenueData(formatted);
      }
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      message.error('Không thể tải dữ liệu doanh thu');
    } finally {
      setLoading(false);
    }
  };

  const fetchContractData = async () => {
    try {
      let startDate, endDate;

      if (dateRange && dateRange.length === 2) {
        startDate = dateRange[0].format('YYYY-MM-DD');
        endDate = dateRange[1].format('YYYY-MM-DD');
      } else {
        const now = new Date();
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      }

      const tenantParam = selectedTenant ? `&tenantId=${encodeURIComponent(selectedTenant)}` : '';
      const response = await fetch(`${REPORT_API.CONTRACTS}?startDate=${startDate}&endDate=${endDate}${tenantParam}`);
      const result = await response.json();

      if (result.data) {
        const summary = result.data.summary || {};
        setContractSummary({
          totalContracts: summary.totalContracts || 0,
          activeContracts: summary.activeContracts || 0,
          totalFactories: summary.totalFactories || 0,
          totalTenants: summary.totalTenants || 0
        });

        const factoryData = (result.data.contractsByFactory || []).map(factory => ({
          FactoryName: factory.FactoryName,
          count: parseInt(factory.count) || 0,
          activeCount: parseInt(factory.activeCount) || 0
        }));

        setContractsByFactory(factoryData);
        setContractsByStatus(result.data.contractsByStatus || []);
        setContractsByTenant(result.data.contractsByTenant || []);
      }
    } catch (error) {
      console.error('Error fetching contract data:', error);
      message.error('Không thể tải dữ liệu hợp đồng');
    }
  };

  const fetchFactoryData = async () => {
    try {
      let startDate, endDate;
      if (dateRange && dateRange.length === 2) {
        startDate = dateRange[0].format('YYYY-MM-DD');
        endDate = dateRange[1].format('YYYY-MM-DD');
      } else {
        const now = new Date();
        startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
      }

      const token = localStorage.getItem('token');
      if (!token) {
        message.error('Vui lòng đăng nhập lại');
        return;
      }

      setLoading(true);

      // Gọi API factories để lấy thông tin nhà xưởng
      const factoryResponse = await fetch(REPORT_API.FACTORIES, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!factoryResponse.ok) {
        throw new Error(`HTTP error! status: ${factoryResponse.status}`);
      }

      const factoryResult = await factoryResponse.json();

      if (!factoryResult.data) {
        throw new Error('Không có dữ liệu nhà xưởng từ API');
      }

      setFactoryData(factoryResult.data.list || []);

      // Gọi API revenue để lấy thông tin doanh thu
      const revenueResponse = await fetch(REPORT_API.REVENUE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          startDate,
          endDate,
          tenantId: selectedTenant || undefined
        })
      });

      if (!revenueResponse.ok) {
        throw new Error(`HTTP error! status: ${revenueResponse.status}`);
      }

      const revenueResult = await revenueResponse.json();

      if (!revenueResult.data) {
        throw new Error('Không có dữ liệu doanh thu từ API');
      }

      const { summary, paymentStats, revenue } = revenueResult.data;
      const factoryStats = factoryResult.data.summary || {};

      if (!summary || !paymentStats || !revenue) {
        throw new Error('Dữ liệu không đầy đủ từ API');
      }

      // Xử lý thống kê thanh toán tiền điện
      const electricityPaidStats = paymentStats.find(item => item.type === 'electricity' && item.Status === 'paid');
      const electricityPendingStats = paymentStats.find(item => item.type === 'electricity' && (item.Status === 'pending' || item.Status === 'unpaid'));
      const electricityPartialStats = paymentStats.find(item => item.type === 'electricity' && item.Status === 'partial');
      const electricityOverdueStats = paymentStats.find(item => item.type === 'electricity' && item.Status === 'overdue');

      const paidElectricityAmount = electricityPaidStats?.totalAmount || 0;
      const unpaidElectricityAmount = electricityPendingStats?.totalAmount || 0;
      const partialElectricityAmount = electricityPartialStats?.totalAmount || 0;
      const overdueElectricityAmount = electricityOverdueStats?.totalAmount || 0;
      const paidElectricityBills = electricityPaidStats?.count;
      const unpaidElectricityBills = electricityPendingStats?.count;
      const electricityPartialBills = electricityPartialStats?.count;
      const electricityOverdueBills = electricityOverdueStats?.count;

      // Xử lý thống kê thanh toán tiền nước
      const waterPaidStats = paymentStats.find(item => item.type === 'water' && item.Status === 'paid');
      const waterPendingStats = paymentStats.find(item => item.type === 'water' && (item.Status === 'pending' || item.Status === 'unpaid'));
      const waterPartialStats = paymentStats.find(item => item.type === 'water' && item.Status === 'partial');
      const waterOverdueStats = paymentStats.find(item => item.type === 'water' && item.Status === 'overdue');

      const paidWaterAmount = waterPaidStats?.totalAmount || 0;
      const unpaidWaterAmount = waterPendingStats?.totalAmount || 0;
      const partialWaterAmount = waterPartialStats?.totalAmount || 0;
      const overdueWaterAmount = waterOverdueStats?.totalAmount || 0;
      const paidWaterBills = waterPaidStats?.count;
      const unpaidWaterBills = waterPendingStats?.count;
      const waterPartialBills = waterPartialStats?.count;
      const waterOverdueBills = waterOverdueStats?.count;

      // Xử lý thống kê thanh toán tiền thuê
      const rentPaidStats = paymentStats.find(item => item.type === 'rent' && item.Status === 'paid');
      const rentOverdueStats = paymentStats.find(item => item.type === 'rent' && item.Status === 'overdue');
      const rentPartialStats = paymentStats.find(item => item.type === 'rent' && item.Status === 'partial');
      const rentUnpaidStats = paymentStats.find(item => item.type === 'rent' && item.Status === 'unpaid');

      const utilityPaidStats = paymentStats.find(item => item.type === 'service' && item.Status === 'paid');
      const utilityOverdueStats = paymentStats.find(item => item.type === 'service' && item.Status === 'overdue');
      const utilityPartialStats = paymentStats.find(item => item.type === 'service' && item.Status === 'partial');
      const utilityUnpaidStats = paymentStats.find(item => item.type === 'service' && item.Status === 'unpaid');

      const paidUtilityAmount = utilityPaidStats?.totalAmount || 0;
      const overdueUtilityAmount = utilityOverdueStats?.totalAmount || 0;
      const partialUtilityAmount = utilityPartialStats?.totalAmount || 0;
      const unpaidUtilityAmount = utilityUnpaidStats?.totalAmount || 0;
      const paidUtilityBills = utilityPaidStats?.count;
      const unpaidUtilityBills = utilityUnpaidStats?.count;
      const utilityOverdueBills = utilityOverdueStats?.count;
      const utilityPartialBills = utilityPartialStats?.count;

      const otherPaidStats = paymentStats.find(item => item.type === 'other' && item.Status === 'paid');
      const otherOverdueStats = paymentStats.find(item => item.type === 'other' && item.Status === 'overdue');
      const otherPartialStats = paymentStats.find(item => item.type === 'other' && item.Status === 'partial');
      const otherUnpaidStats = paymentStats.find(item => item.type === 'other' && (item.Status === 'unpaid' || item.Status === 'pending'));

      const paidOtherAmount = otherPaidStats?.totalAmount || 0;
      const overdueOtherAmount = otherOverdueStats?.totalAmount || 0;
      const partialOtherAmount = otherPartialStats?.totalAmount || 0;
      const unpaidOtherAmount = otherUnpaidStats?.totalAmount || 0;
      const paidOtherBills = otherPaidStats?.count;
      const unpaidOtherBills = otherUnpaidStats?.count;
      const otherOverdueBills = otherOverdueStats?.count;
      const otherPartialBills = otherPartialStats?.count;

      const paidRentAmount = rentPaidStats?.totalAmount || 0;
      const overdueRentAmount = rentOverdueStats?.totalAmount || 0;
      const partialRentAmount = rentPartialStats?.totalAmount || 0;
      const unpaidRentStats = rentUnpaidStats?.totalAmount || 0;
      const unpaidRentAmount = unpaidRentStats;
      const paidRentBills = rentPaidStats?.count;
      const unpaidRentBills = rentUnpaidStats?.count;
      const rentOverdueBills = rentOverdueStats?.count;
      const rentPartialBills = rentPartialStats?.count;

      const getCountByStatus = (status) => paymentStats.find(item => item.type === 'rent' && item.Status === status)?.count || 0;

      const totalPaidBills = getCountByStatus('paid');
      const totalUnpaidBills = getCountByStatus('unpaid') + getCountByStatus('pending');
      const totalPartialBills = getCountByStatus('partial');
      const totalOverdueBills = getCountByStatus('overdue');

      const totalElectricityAmount = summary.totalElectricityAmount;
      const totalWaterAmount = summary.totalWaterAmount;
      const totalRentAmount = summary.totalRentAmount;
      const totalUtilityAmount = summary.totalUtilityAmount;

      const totalInvoiceStats = paymentStats.filter(item => item.type === 'invoiceTotal');
      const totalMoney = summary.totalRevenue || (totalInvoiceStats.length > 0
        ? totalInvoiceStats.reduce((acc, item) => acc + item.totalAmount, 0)
        : paymentStats.reduce((acc, item) => acc + item.totalAmount, 0));

      const totalPaidAmount = summary.totalPaid || (totalInvoiceStats.length > 0
        ? totalInvoiceStats.reduce((acc, item) => acc + (item.paidAmount || 0), 0)
        : paymentStats.reduce((acc, item) => acc + (item.paidAmount || 0), 0));

      const totalUnpaidRevenue = totalMoney - totalPaidAmount;
      const totalUnpaidAmount = totalUnpaidRevenue;

      setDashboardData({
        // Thông tin nhà xưởng
        totalFactories: factoryStats?.totalFactories || 0,
        occupiedFactories: factoryStats?.rentedFactories || 0,
        totalArea: factoryStats?.totalArea || 0,
        occupiedArea: factoryStats?.rentedArea || 0,
        areaUtilization: (factoryStats?.totalArea > 0) ? ((factoryStats.rentedArea / factoryStats.totalArea) * 100) : 0,

        // Thông tin tổng quan từ summary
        totalRevenue: totalMoney,
        totalPaidAmount: totalPaidAmount,
        totalUnpaidRevenue: totalUnpaidRevenue,
        totalElectricityRevenue: summary.totalElectricityRevenue,
        totalWaterRevenue: summary.totalWaterRevenue,
        totalRentRevenue: summary.totalRentRevenue,
        totalUtilityRevenue: summary.totalUtilityRevenue,
        totalOtherRevenue: summary.totalOtherRevenue || (paidOtherAmount + unpaidOtherAmount + overdueOtherAmount + partialOtherAmount),

        // Thông tin về số lượng hóa đơn
        totalElectricityBills: summary.totalElectricityBills,
        totalWaterBills: summary.totalWaterBills,
        totalUtilityBills: summary.totalUtilityBills,

        // Thông tin về hợp đồng
        averageContractCount: summary.averageContractCount,
        maxContractCount: summary.maxContractCount,
        averageTenantCount: summary.averageTenantCount,
        maxTenantCount: summary.maxTenantCount,
        averageFactoryCount: summary.averageFactoryCount,
        maxFactoryCount: summary.maxFactoryCount,

        // Thông tin về giá trị trung bình và max/min
        averageRentAmount: summary.averageRentAmount,
        maxRentAmount: summary.maxRentAmount,
        minRentAmount: summary.minRentAmount,

        // Thông tin thanh toán tiền điện
        paidElectricityAmount,
        unpaidElectricityAmount,
        partialElectricityAmount,
        overdueElectricityAmount,
        paidElectricityBills,
        unpaidElectricityBills,
        electricityPartialBills,
        electricityOverdueBills,
        // Thông tin thanh toán tiền nước
        paidWaterAmount,
        unpaidWaterAmount,
        partialWaterAmount,
        overdueWaterAmount,
        paidWaterBills,
        unpaidWaterBills,
        waterPartialBills,
        waterOverdueBills,
        // Thông tin thanh toán tiền thuê
        paidRentAmount,
        unpaidRentAmount,
        overdueRentAmount,
        partialRentAmount,
        paidRentBills,
        unpaidRentBills,
        rentOverdueBills,
        rentPartialBills,
        // Thông tin thanh toán tiền dịch vụ
        paidUtilityAmount,
        unpaidUtilityAmount,
        overdueUtilityAmount,
        paidUtilityBills,
        partialUtilityAmount,
        unpaidUtilityBills,
        utilityOverdueBills,
        utilityPartialBills,
        // Thông tin thanh toán chi phí khác
        paidOtherAmount,
        unpaidOtherAmount,
        overdueOtherAmount,
        partialOtherAmount,
        paidOtherBills,
        unpaidOtherBills,
        otherOverdueBills,
        otherPartialBills,
        // Thông tin tổng thanh toán
        totalPaidAmount: totalPaidAmount,
        totalUnpaidRevenue: totalUnpaidAmount,
        totalOverdueRevenue: overdueElectricityAmount + overdueWaterAmount + overdueRentAmount + overdueUtilityAmount + overdueOtherAmount,
        totalPartialRevenue: partialElectricityAmount + partialWaterAmount + partialRentAmount + partialUtilityAmount + partialOtherAmount,
        totalPaidBills,
        totalUnpaidBills,
        totalElectricityAmount,
        totalWaterAmount,
        totalRentAmount,
        totalUtilityAmount,
        totalOverdueBills,
        totalPartialBills,
        // Thông tin bổ sung
        occupancyRate: factoryStats?.areaUtilization || 0,
        newContracts: summary.maxContractCount - summary.averageContractCount
      });

      // Cập nhật dữ liệu cho biểu đồ - format lại theo tháng
      const formattedRevenue = revenue.map(item => {
        const rentRevenue = item.rentRevenue || 0;
        const electricityRevenue = item.electricityRevenue || 0;
        const waterRevenue = item.waterRevenue || 0;
        const totalRevenue = item.totalRevenue || 0;
        const otherRevenue =
          item.otherRevenue ?? Math.max(0, totalRevenue - rentRevenue - electricityRevenue - waterRevenue);

        return {
          month: String(item.month).padStart(2, '0'),
          revenue: totalRevenue,
          contracts: item.contracts || item.contractCount || 0,
          rentRevenue,
          // utilityRevenue đang là tổng điện nước (nếu backend có trả)
          utilityRevenue: item.utilityRevenue,
          electricityRevenue,
          waterRevenue,
          otherRevenue,
          electricityBillCount: item.electricityBillCount,
          waterBillCount: item.waterBillCount
        };
      });

      setRevenueData(formattedRevenue);

    } catch (error) {
      console.error('Error fetching data:', error);
      message.error(`Không thể tải dữ liệu: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchUtilityData = async () => {
    try {
      setLoading(true);
      let startDate, endDate;

      if (dateRange && dateRange.length === 2) {
        startDate = dateRange[0].format('YYYY-MM-DD');
        endDate = dateRange[1].format('YYYY-MM-DD');
      } else {
        const now = new Date();
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      }

      const tenantParam = selectedTenant ? `&tenantId=${encodeURIComponent(selectedTenant)}` : '';
      const response = await fetch(`${REPORT_API.UTILITIES}?startDate=${startDate}&endDate=${endDate}${tenantParam}`);
      const result = await response.json();

      if (result.data && result.data.monthlyStats) {
        setUtilityData(result.data.monthlyStats);
      }
    } catch (error) {
      console.error('Error fetching utility data:', error);
      message.error('Không thể tải dữ liệu điện nước');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token'); // Get token from localStorage

      if (!token) {
        message.error('Không tìm thấy token xác thực');
        return;
      }

      const response = await fetch(TENANT_API.ALL, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.tenants) { // Sửa từ result.data thành result.tenants
        setTenants(result.tenants.map(tenant => ({
          id: tenant.TenantID,
          name: tenant.TenantName
        })));
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      message.error('Không thể tải danh sách khách thuê');
    }
  };
  const fetchTenantReport = async () => {
    if (!selectedTenant) {
      message.info('Vui lòng chọn khách thuê');
      return;
    }

    try {
      setLoading(true);
      let startDate, endDate;

      if (tenantDateRange && tenantDateRange.length === 2) {
        startDate = tenantDateRange[0].format('YYYY-MM-DD');
        endDate = tenantDateRange[1].format('YYYY-MM-DD');
      } else {
        const now = new Date();
        startDate = new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
      }

      const response = await fetch(REPORT_API.TENANT_REPORT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantId: selectedTenant,
          startDate,
          endDate
        }),
      });

      const result = await response.json();
      if (result.data) {
        // Định dạng dữ liệu từ API để hiển thị trên biểu đồ
        const formattedMonthlyData = result.data.monthlyData?.map(item => ({
          month: String(item.month).padStart(2, '0') + '/' + item.year,
          monthName: item.monthName + '/' + item.year,
          year: item.year,
          electricityAmount: item.electricityAmount || 0,
          electricityUsage: item.electricityUsage || 0,
          waterAmount: item.waterAmount || 0,
          waterUsage: item.waterUsage || 0,
          otherAmount: item.otherAmount || 0,
          rentAmount: item.rentAmount || 0,
          rentPaid: item.rentPaid || 0
        })) || [];

        setTenantReportData({
          tenantName: tenants.find(tenant => tenant.id === selectedTenant)?.name || '',
          summary: result.data.summary,
          details: result.data.details,
          monthlyData: formattedMonthlyData,
        });
      }
    } catch (error) {
      console.error('Error fetching tenant report:', error);
      message.error('Không thể tải báo cáo cho khách thuê này');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchContractData();
    fetchRevenueData();
    fetchFactoryData();
    fetchUtilityData();
    fetchTenants();
    fetchAllInvoices();
  }, []);

  const fetchAllInvoices = async (params = {}) => {
    try {
      setLoading(true);
      const filtersToUse = params.filters || filters;

      // Fetch rent invoices (backend currently caps to 100 per page -> must paginate)
      const rentInvoicesRaw = [];
      let rentPage = 1;
      let rentTotalPages = 1;
      // Don't rely on a big "limit". Always paginate until exhausted.
      const rentPageSizeHint = params.pageSize || 100;
      const maxRentPagesSafety = 10000;

      while (rentPage <= rentTotalPages && rentPage <= maxRentPagesSafety) {
        // eslint-disable-next-line no-await-in-loop
        const rentResponse = await axios.get(RENT_API.INVOICES.LIST, {
          params: {
            ...filtersToUse,
            page: rentPage,
            limit: rentPageSizeHint,
          },
        });

        const rentData = rentResponse?.data?.data;
        const rentPageInvoices = rentData?.invoices || [];
        rentInvoicesRaw.push(...rentPageInvoices);

        const paginationMeta = rentData?.pagination;
        // If backend sends pagination, trust it; otherwise stop when a page returns 0 items.
        if (paginationMeta?.totalPages) {
          rentTotalPages = paginationMeta.totalPages;
        } else if (rentPageInvoices.length === 0) {
          break;
        }

        rentPage += 1;
      }

      // Tạo một Map để lưu trữ các hóa đơn duy nhất dựa trên ID
      const uniqueInvoicesMap = new Map();

      // Xử lý hóa đơn thuê/tổng hợp
      const rentInvoices = rentInvoicesRaw.map(item => ({
        id: item.id || `rent_${item.InvoiceID}`,
        invoiceNumber: item.invoiceNumber || item.InvoiceID,
        factoryName: item.factoryName || item.FactoryName,
        tenantName: item.tenantName || item.TenantName,
        tenantID: item.tenantID || item.TenantID,
        contractID: item.contractId || item.ContractID,
        period: item.period || item.Period,
        amount: item.amount || item.Amount || item.totalAmount || 0,
        paidAmount: item.paidAmount || item.PaidAmount || 0,
        remainingAmount: (item.amount || item.Amount || item.totalAmount || 0) - (item.paidAmount || item.PaidAmount || 0),
        dueDate: item.dueDate || item.DueDate ? moment(item.dueDate || item.DueDate).format('DD/MM/YYYY') : 'Chưa có',
        status: (item.status || item.Status) === 'unpaid' ? 'pending' : (item.status || item.Status),
        invoiceType: item.invoiceType || item.InvoiceType || 'monthly',
        type: item.invoiceType || item.InvoiceType || 'monthly'
      }));

      // Thêm hóa đơn thuê vào Map
      rentInvoices.forEach(invoice => {
        uniqueInvoicesMap.set(invoice.id, invoice);
      });

      // Chuyển Map thành mảng để hiển thị
      const allInvoices = Array.from(uniqueInvoicesMap.values());


      // Lưu toàn bộ data và để Table tự xử lý pagination
      setData(allInvoices);
      setPagination(prev => ({
        ...prev,
        total: allInvoices.length,
        current: params.current || prev.current,
        pageSize: params.pageSize || prev.pageSize
      }));
    } catch (error) {
      console.error('Fetch invoices error:', error);
      message.error('Không thể tải danh sách hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination, tableFilters, sorter) => {
    // Chỉ cập nhật pagination state, không gọi lại API
    // Vì data đã được load đầy đủ và Table sẽ tự xử lý pagination
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleSearch = (values) => {
    const formattedValues = { ...values };
    if (formattedValues.startDate) {
      formattedValues.startDate = formattedValues.startDate.format('DD/MM/YYYY');
    }
    if (formattedValues.endDate) {
      formattedValues.endDate = formattedValues.endDate.format('DD/MM/YYYY');
    }
    setFilters(formattedValues);
    setPagination({ ...pagination, current: 1 });
    fetchAllInvoices({
      current: 1,
      pageSize: pagination.pageSize,
      filters: formattedValues
    });
  };

  // Hàm xuất Excel cho hóa đơn
  const handleExportExcel = async () => {
    try {
      setLoading(true);

      // Lấy dữ liệu hiện tại từ form
      const formValues = form.getFieldsValue();
      const exportData = {
        ...formValues,
        invoiceType: selectedInvoiceType,
        tenantID: selectedTenant,
        startDate: formValues.startDate ? formValues.startDate.format('DD/MM/YYYY') : undefined,
        endDate: formValues.endDate ? formValues.endDate.format('DD/MM/YYYY') : undefined,
      };

      // Gọi API xuất Excel
      const response = await axios.post(REPORT_API.EXPORT_EXCEL, exportData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      // Tạo file download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Tạo tên file với timestamp
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const fileName = `Bao_cao_hoa_don_${timestamp}.xlsx`;

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('Xuất Excel thành công!');
    } catch (error) {
      console.error('Lỗi khi xuất Excel:', error);
      message.error('Không thể xuất file Excel. Vui lòng thử lại!');
    } finally {
      setLoading(false);
    }
  };
  const handleTenantDateRangeChange = (dates) => {
    setTenantDateRange(dates);
  };

  const handleTenantChange = (value) => {
    setSelectedTenant(value);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const handleGeneralDateRangeChange = (dates) => {
    setDateRange(dates || []);
  };

  const handleApplyGeneralFilters = async () => {
    await Promise.allSettled([
      fetchContractData(),
      fetchRevenueData(),
      fetchFactoryData(),
      fetchUtilityData(),
    ]);
  };

  const handleResetGeneralFilters = async () => {
    setDateRange([]);
    setSelectedTenant(null);
    await Promise.allSettled([
      fetchContractData(),
      fetchRevenueData(),
      fetchFactoryData(),
      fetchUtilityData(),
    ]);
  };

  const renderGeneralTab = () => (
    <>
      <Card
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: isMobile ? 12 : 16 }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={10}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontWeight: 600 }}>Từ ngày - Đến ngày</label>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={handleGeneralDateRangeChange}
                format="DD/MM/YYYY"
                allowClear
              />
            </div>
          </Col>
          {/* <Col xs={24} md={10}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontWeight: 600 }}>Khách hàng</label>
              <Select
                placeholder="Chọn khách thuê"
                style={{ width: '100%' }}
                value={selectedTenant}
                onChange={handleTenantChange}
                allowClear
                showSearch
                optionFilterProp="label"
                options={tenants.map(tenant => ({ value: tenant.id, label: tenant.name }))}
              />
            </div>
          </Col> */}
          <Col
            xs={24}
            md={4}
            style={{
              paddingLeft: 60,
              paddingRight: 6,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              marginTop: 25,
              marginLeft: isMobile ? 0 : 60
            }}
          >
            <Space
              style={{
                width: '100%',
                justifyContent: 'flex-end',
                display: 'flex',
                flexWrap: 'nowrap'
              }}
            >
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleApplyGeneralFilters}
                loading={loading}
              >
                Áp dụng
              </Button>
              <Button
                icon={<DeleteOutlined />}
                onClick={handleResetGeneralFilters}
                disabled={loading}
              >
                Xóa lọc
              </Button>
            </Space>
          </Col>
        </Row>
        {(dateRange && dateRange.length === 2) && (
          <div style={{ marginTop: 10, color: '#666' }}>
            Đang lọc từ ngày <strong>{dateRange[0].format('DD/MM/YYYY')}</strong> đến ngày <strong>{dateRange[1].format('DD/MM/YYYY')}</strong>
            {selectedTenant ? (
              <>
                {' '}| Khách hàng: <strong>{tenants.find(t => t.id === selectedTenant)?.name || selectedTenant}</strong>
              </>
            ) : null}
          </div>
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={8}>
          <Card
            style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
            bordered={true}
            hoverable
            loading={loading}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
              <Tooltip title="Tổng doanh thu (tất cả các khoản)">
                <span>Tổng doanh thu</span>
              </Tooltip>
            </div>
            <div style={{ fontSize: '22px', color: '#0f172a', fontWeight: '700' }}>
              {(dashboardData?.totalRevenue || 0).toLocaleString('vi-VN')} VNĐ
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Card
            style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
            bordered={true}
            hoverable
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
              Số nhà xưởng đã thuê
            </div>
            <div style={{ fontSize: '22px', color: '#0f172a', fontWeight: '700' }}>
              {dashboardData?.occupiedFactories || 0} / {dashboardData?.totalFactories || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Card
            style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
            bordered={true}
            hoverable
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
              Tỷ lệ lấp đầy
            </div>
            <div style={{ fontSize: '22px', color: '#0f172a', fontWeight: '700' }}>
              {(dashboardData?.areaUtilization || 0).toFixed(2)}%
            </div>
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={6}>
          <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }} bodyStyle={{ padding: '16px' }}>
            <div style={{ color: '#64748b', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Tổng số hợp đồng</div>
            <div style={{ fontSize: 22, color: '#0f172a', fontWeight: '700' }}>{contractSummary?.totalContracts || 0}</div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }} bodyStyle={{ padding: '16px' }}>
            <div style={{ color: '#64748b', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Hợp đồng đang hoạt động</div>
            <div style={{ fontSize: 22, color: '#16a34a', fontWeight: '700' }}>{contractSummary?.activeContracts || 0}</div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }} bodyStyle={{ padding: '16px' }}>
            <div style={{ color: '#64748b', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Tổng số nhà xưởng</div>
            <div style={{ fontSize: 22, color: '#0f172a', fontWeight: '700' }}>{contractSummary?.totalFactories || 0}</div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }} bodyStyle={{ padding: '16px' }}>
            <div style={{ color: '#64748b', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Tổng số khách thuê</div>
            <div style={{ fontSize: 22, color: '#0f172a', fontWeight: '700' }}>{contractSummary?.totalTenants || 0}</div>
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="Thống kê thanh toán chi tiết" bordered={false} style={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <Row gutter={[16, 16]} align="stretch">
              <Col xs={24} lg={16}>
                <Row gutter={[16, 16]} align="stretch">
                  <Col xs={24} sm={12} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Card
                      bordered={true}
                      style={{
                        width: '100%',
                        flex: 1,
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                      }}
                      bodyStyle={{
                        padding: '20px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                      hoverable
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          color: '#334155'
                        }}>
                          🏭
                        </div>
                        <div>
                          <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                            Tiền thuê nhà xưởng
                          </div>
                          <div style={{ fontSize: '20px', color: '#0f172a', fontWeight: '700' }}>
                            {((dashboardData?.paidRentAmount || 0) + (dashboardData?.unpaidRentAmount || 0) + (dashboardData?.overdueRentAmount || 0) + (dashboardData?.partialRentAmount || 0)).toLocaleString('vi-VN')} VNĐ
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card
                      bordered={true}
                      style={{
                        width: '100%',
                        flex: 1,
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                      }}
                      bodyStyle={{
                        padding: '20px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                      hoverable
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          background: '#fffbeb',
                          border: '1px solid #fef3c7',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          color: '#d97706'
                        }}>
                          🔧
                        </div>
                        <div>
                          <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                            Tiền dịch vụ & hạ tầng
                          </div>
                          <div style={{ fontSize: '20px', color: '#0f172a', fontWeight: '700' }}>
                            {((dashboardData?.paidUtilityAmount || 0) + (dashboardData?.unpaidUtilityAmount || 0) + (dashboardData?.overdueUtilityAmount || 0) + (dashboardData?.partialUtilityAmount || 0)).toLocaleString('vi-VN')} VNĐ
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>

                  <Col xs={24} sm={12} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Card
                      bordered={true}
                      style={{
                        width: '100%',
                        flex: 1,
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                      }}
                      bodyStyle={{
                        padding: '20px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                      hoverable
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          background: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          color: '#16a34a'
                        }}>
                          ⚡
                        </div>
                        <div>
                          <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                            Tiền điện
                          </div>
                          <div style={{ fontSize: '20px', color: '#0f172a', fontWeight: '700' }}>
                            {((dashboardData?.paidElectricityAmount || 0) + (dashboardData?.unpaidElectricityAmount || 0) + (dashboardData?.overdueElectricityAmount || 0) + (dashboardData?.partialElectricityAmount || 0)).toLocaleString('vi-VN')} VNĐ
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card
                      bordered={true}
                      style={{
                        width: '100%',
                        flex: 1,
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                      }}
                      bodyStyle={{
                        padding: '20px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                      hoverable
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          background: '#f0f9ff',
                          border: '1px solid #bae6fd',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          color: '#0284c7'
                        }}>
                          💧
                        </div>
                        <div>
                          <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                            Tiền nước sạch
                          </div>
                          <div style={{ fontSize: '20px', color: '#0f172a', fontWeight: '700' }}>
                            {((dashboardData?.paidWaterAmount || 0) + (dashboardData?.unpaidWaterAmount || 0) + (dashboardData?.overdueWaterAmount || 0) + (dashboardData?.partialWaterAmount || 0)).toLocaleString('vi-VN')} VNĐ
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card
                      bordered={true}
                      style={{
                        width: '100%',
                        flex: 1,
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                      }}
                      bodyStyle={{
                        padding: '20px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                      hoverable
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          background: '#faf5ff',
                          border: '1px solid #f3e8ff',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          color: '#9333ea'
                        }}>
                          🏷️
                        </div>
                        <div>
                          <div style={{ color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                            Tiền nước thải
                          </div>
                          <div style={{ fontSize: '20px', color: '#0f172a', fontWeight: '700' }}>
                            {((dashboardData?.paidOtherAmount || 0) + (dashboardData?.unpaidOtherAmount || 0) + (dashboardData?.overdueOtherAmount || 0) + (dashboardData?.partialOtherAmount || 0)).toLocaleString('vi-VN')} VNĐ
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Col>

              <Col xs={24} lg={8} style={{ display: 'flex' }}>
                <Card
                  bordered={false}
                  style={{
                    width: '100%',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                    color: '#ffffff'
                  }}
                  bodyStyle={{
                    padding: '24px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '500' }}>Tổng cộng doanh thu</span>
                      <span style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        color: '#f1f5f9'
                      }}>
                        Tất cả hóa đơn
                      </span>
                    </div>
                    <div style={{ fontSize: '26px', color: '#ffffff', fontWeight: '700', marginBottom: '20px' }}>
                      {(dashboardData?.totalRevenue || 0).toLocaleString('vi-VN')} VNĐ
                    </div>

                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '10px',
                      padding: '14px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>Đã thanh toán:</span>
                        <span style={{ color: '#4ade80', fontWeight: '600', fontSize: '13px' }}>
                          {(dashboardData?.totalPaidAmount || 0).toLocaleString('vi-VN')} VNĐ
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>Chưa thanh toán:</span>
                        <span style={{ color: '#fb7185', fontWeight: '600', fontSize: '13px' }}>
                          {(dashboardData?.totalUnpaidRevenue || 0).toLocaleString('vi-VN')} VNĐ
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>Quá hạn:</span>
                        <span style={{ color: '#facc15', fontWeight: '600', fontSize: '13px' }}>
                          {(dashboardData?.totalOverdueRevenue || 0).toLocaleString('vi-VN')} VNĐ
                        </span>
                      </div>
                      <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '8px', marginTop: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>HĐ đã TT:</span>
                          <span style={{ color: '#e2e8f0', fontSize: '12px' }}>{dashboardData?.totalPaidBills || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>HĐ chưa TT:</span>
                          <span style={{ color: '#e2e8f0', fontSize: '12px' }}>{(dashboardData?.totalUnpaidBills + dashboardData?.totalPartialBills) || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>HĐ quá hạn:</span>
                          <span style={{ color: '#e2e8f0', fontSize: '12px' }}>{dashboardData?.totalOverdueBills || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ margin: 0, color: '#1890ff', fontWeight: 'bold' }}>
                    📊 Biểu đồ doanh thu theo tháng
                  </h3>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
                    Phân tích chi tiết doanh thu từ các nguồn khác nhau
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Tag color="blue" icon={<span>💰</span>}>
                    Tổng: {revenueData.reduce((sum, item) => sum + (item.revenue || 0), 0).toLocaleString('vi-VN')} VNĐ
                  </Tag>
                  <Tag color="green" icon={<span>📈</span>}>
                    TB/tháng: {(revenueData.reduce((sum, item) => sum + (item.revenue || 0), 0) / Math.max(revenueData.length, 1)).toLocaleString('vi-VN')} VNĐ
                  </Tag>
                </div>
              </div>
            }
            loading={loading}
            bordered={false}
            bodyStyle={{
              padding: '20px',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              borderRadius: '12px'
            }}
            style={{
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              borderRadius: '12px'
            }}
          >
            <ResponsiveContainer width="99%" height={isMobile ? 260 : 400}>
              <ComposedChart
                data={revenueData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="month"
                  label={{
                    value: 'Tháng',
                    position: 'bottom',
                    style: { textAnchor: 'middle', fill: '#666', fontSize: '14px', fontWeight: 'bold' }
                  }}
                  tick={{ fontSize: 12, fill: '#333' }}
                  tickLine={{ stroke: '#666' }}
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={formatRevenue}
                  domain={[0, dataMax => Math.ceil(dataMax * 1.2)]}
                  label={{
                    value: 'Doanh thu (VNĐ)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: '#666', fontSize: '14px', fontWeight: 'bold' }
                  }}
                  tick={{ fontSize: 12, fill: '#333' }}
                  tickLine={{ stroke: '#666' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, dataMax => Math.ceil(dataMax * 1.2)]}
                  label={{
                    value: 'Số hợp đồng',
                    angle: 90,
                    position: 'insideRight',
                    style: { textAnchor: 'middle', fill: '#666', fontSize: '14px', fontWeight: 'bold' }
                  }}
                  tick={{ fontSize: 12, fill: '#333' }}
                  tickLine={{ stroke: '#666' }}
                />
                <RechartsTooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const totalRevenue = data.revenue || 0;
                      const rentRevenue = data.rentRevenue || 0;
                      const electricityRevenue = data.electricityRevenue || 0;
                      const waterRevenue = data.waterRevenue || 0;
                      const utilityRevenue = data.utilityRevenue || 0;
                      const otherRevenue = data.otherRevenue || 0;
                      const contracts = data.contracts || 0;

                      return (
                        <div style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          padding: '16px',
                          border: '2px solid #1890ff',
                          borderRadius: '12px',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                          backdropFilter: 'blur(10px)',
                          minWidth: '280px'
                        }}>
                          <div style={{
                            background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            marginBottom: '12px',
                            textAlign: 'center',
                            fontWeight: 'bold'
                          }}>
                            📅 Tháng {label}
                          </div>

                          <div style={{ marginBottom: '12px' }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '8px',
                              padding: '8px',
                              background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                              borderRadius: '6px',
                              color: 'white'
                            }}>
                              <span style={{ fontWeight: 'bold' }}>💰 Tổng doanh thu:</span>
                              <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                {totalRevenue.toLocaleString('vi-VN')} VNĐ
                              </span>
                            </div>
                          </div>

                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '8px',
                            marginBottom: '12px'
                          }}>
                            <div style={{
                              padding: '6px 8px',
                              background: 'rgba(24, 144, 255, 0.1)',
                              borderRadius: '4px',
                              border: '1px solid #1890ff'
                            }}>
                              <div style={{ color: '#1890ff', fontSize: '12px', fontWeight: 'bold' }}>🏭 Thuê xưởng & dịch vụ</div>
                              <div style={{ color: '#333', fontSize: '14px', fontWeight: '600' }}>
                                {rentRevenue.toLocaleString('vi-VN')} VNĐ
                              </div>
                            </div>

                            <div style={{
                              padding: '6px 8px',
                              background: 'rgba(82, 196, 26, 0.1)',
                              borderRadius: '4px',
                              border: '1px solid #52c41a'
                            }}>
                              <div style={{ color: '#52c41a', fontSize: '12px', fontWeight: 'bold' }}>⚡💧 Tổng điện nước</div>
                              <div style={{ color: '#333', fontSize: '14px', fontWeight: '600' }}>
                                {utilityRevenue.toLocaleString('vi-VN')} VNĐ
                              </div>
                            </div>
                          </div>

                          <div style={{ marginBottom: '12px' }}>
                            <div style={{
                              padding: '6px 8px',
                              background: 'rgba(114, 46, 209, 0.1)',
                              borderRadius: '4px',
                              border: '1px solid #722ed1'
                            }}>
                              <div style={{ color: '#722ed1', fontSize: '12px', fontWeight: 'bold' }}>🔧 Tiền nước thải</div>
                              <div style={{ color: '#333', fontSize: '14px', fontWeight: '600' }}>
                                {otherRevenue.toLocaleString('vi-VN')} VNĐ
                              </div>
                            </div>
                          </div>

                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '6px',
                            marginBottom: '12px'
                          }}>
                            <div style={{
                              padding: '4px 6px',
                              background: 'rgba(24, 144, 255, 0.1)',
                              borderRadius: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ color: '#1890ff', fontSize: '10px' }}>⚡ Điện</div>
                              <div style={{ color: '#333', fontSize: '12px', fontWeight: '600' }}>
                                {electricityRevenue.toLocaleString('vi-VN')}
                              </div>
                            </div>

                            <div style={{
                              padding: '4px 6px',
                              background: 'rgba(82, 196, 26, 0.1)',
                              borderRadius: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ color: '#52c41a', fontSize: '10px' }}>💧 Nước sạch</div>
                              <div style={{ color: '#333', fontSize: '12px', fontWeight: '600' }}>
                                {waterRevenue.toLocaleString('vi-VN')}
                              </div>
                            </div>
                          </div>

                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px',
                            background: 'linear-gradient(135deg, #722ed1 0%, #eb2f96 100%)',
                            borderRadius: '6px',
                            color: 'white'
                          }}>
                            <span style={{ fontWeight: 'bold' }}>📋 Số hợp đồng:</span>
                            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{contracts}</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend
                  verticalAlign="top"
                  height={50}
                  wrapperStyle={{
                    paddingBottom: '10px'
                  }}
                />

                {/* Bar cho doanh thu thuê */}
                <Bar
                  yAxisId="left"
                  dataKey="rentRevenue"
                  name="🏭 Doanh thu thuê"
                  fill="#1890ff"
                  barSize={25}
                  radius={[6, 6, 0, 0]}
                  stackId="a"
                />

                {/* Bar cho doanh thu điện */}
                <Bar
                  yAxisId="left"
                  dataKey="electricityRevenue"
                  name="⚡ Doanh thu điện"
                  fill="#52c41a"
                  barSize={25}
                  radius={[6, 6, 0, 0]}
                  stackId="a"
                />

                {/* Bar cho doanh thu nước */}
                <Bar
                  yAxisId="left"
                  dataKey="waterRevenue"
                  name="💧 Doanh thu nước sạch"
                  fill="#fa8c16"
                  barSize={25}
                  radius={[6, 6, 0, 0]}
                  stackId="a"
                />

                {/* Bar cho doanh thu chi phí khác */}
                <Bar
                  yAxisId="left"
                  dataKey="otherRevenue"
                  name="🔧 Tiền nước thải"
                  fill="#722ed1"
                  barSize={25}
                  radius={[6, 6, 0, 0]}
                  stackId="a"
                />


                {/* Line cho số hợp đồng */}
                <Line
                  yAxisId="right"
                  dataKey="contracts"
                  name="📋 Số hợp đồng"
                  stroke="#722ed1"
                  strokeWidth={3}
                  dot={{
                    fill: '#722ed1',
                    strokeWidth: 2,
                    r: 6,
                    stroke: '#fff'
                  }}
                  activeDot={{
                    r: 8,
                    stroke: '#722ed1',
                    strokeWidth: 2,
                    fill: '#fff'
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Thống kê tổng quan */}
            <div style={{
              marginTop: '20px',
              marginBottom: '20px',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
              gap: '12px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                color: 'white',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>Tổng doanh thu</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  {revenueData.reduce((sum, item) => sum + (item.revenue || 0), 0).toLocaleString('vi-VN')} VNĐ
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                color: 'white',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>TB/tháng</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  {(revenueData.reduce((sum, item) => sum + (item.revenue || 0), 0) / Math.max(revenueData.length, 1)).toLocaleString('vi-VN')} VNĐ
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                color: 'white',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>Tổng hợp đồng</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  {revenueData.reduce((sum, item) => sum + (item.contracts || 0), 0)}
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
                color: 'white',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>Tháng cao nhất</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  {revenueData.length > 0 ?
                    `Tháng ${revenueData.reduce((max, item) =>
                      (item.revenue || 0) > (max.revenue || 0) ? item : max
                    ).month}` : 'N/A'
                  }
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 22 }}>💡</span>
                <span style={{ color: '#13c2c2', fontWeight: 'bold', fontSize: 18 }}>
                  Biểu đồ chi phí và tiêu thụ điện nước theo tháng
                </span>
              </div>
            }
            loading={loading}
            bordered={false}
            bodyStyle={{
              padding: '20px',
              background: 'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)',
              borderRadius: '12px'
            }}
            style={{
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              borderRadius: '12px',
              marginBottom: 24
            }}
          >
            <ResponsiveContainer width="99%" height={isMobile ? 260 : 340}>
              <ComposedChart
                data={revenueData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="month"
                  label={{ value: 'Tháng', position: 'bottom', style: { fill: '#666', fontWeight: 'bold', fontSize: 14 } }}
                  tick={{ fontSize: 12, fill: '#333' }}
                  tickLine={{ stroke: '#666' }}
                />
                <YAxis
                  yAxisId="amount"
                  orientation="left"
                  label={{ value: 'Số tiền (VNĐ)', angle: -90, position: 'insideLeft', style: { fill: '#13c2c2', fontWeight: 'bold', fontSize: 14 } }}
                  tickFormatter={(value) => `${(value / 1000000).toFixed(0)}tr`}
                  tick={{ fontSize: 12, fill: '#13c2c2' }}
                  tickLine={{ stroke: '#13c2c2' }}
                />
                <YAxis
                  yAxisId="bills"
                  orientation="right"
                  label={{ value: 'Số hóa đơn', angle: 90, position: 'insideRight', style: { fill: '#fa8c16', fontWeight: 'bold', fontSize: 14 } }}
                  tick={{ fontSize: 12, fill: '#fa8c16' }}
                  tickLine={{ stroke: '#fa8c16' }}
                />
                <RechartsTooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div style={{
                          background: 'rgba(255,255,255,0.97)',
                          border: '2px solid #13c2c2',
                          borderRadius: 12,
                          boxShadow: '0 4px 16px rgba(19,194,194,0.08)',
                          padding: 16,
                          minWidth: 220
                        }}>
                          <div style={{ fontWeight: 'bold', color: '#13c2c2', marginBottom: 8 }}>
                            📅 Tháng {label}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <span style={{ color: '#1890ff', fontWeight: 600 }}>⚡ Tiền điện: </span>
                            <span>{(data.electricityRevenue || 0).toLocaleString('vi-VN')} VNĐ</span>
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <span style={{ color: '#52c41a', fontWeight: 600 }}>💧 Tiền nước sạch: </span>
                            <span>{(data.waterRevenue || 0).toLocaleString('vi-VN')} VNĐ</span>
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <span style={{ color: '#722ed1', fontWeight: 600 }}>⚡ Số HĐ điện: </span>
                            <span>{data.electricityBillCount || 0}</span>
                          </div>
                          <div>
                            <span style={{ color: '#eb2f96', fontWeight: 600 }}>💧 Số HĐ nước sạch: </span>
                            <span>{data.waterBillCount || 0}</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend
                  iconType="circle"
                  verticalAlign="top"
                  height={40}
                  wrapperStyle={{ paddingBottom: 10, fontWeight: 'bold', fontSize: 14 }}
                />
                <Bar
                  yAxisId="amount"
                  dataKey="electricityRevenue"
                  name="⚡ Tiền điện"
                  fill="#1890ff"
                  maxBarSize={40}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="amount"
                  dataKey="waterRevenue"
                  name="💧 Tiền nước sạch"
                  fill="#52c41a"
                  maxBarSize={40}
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="bills"
                  type="monotone"
                  dataKey="electricityBillCount"
                  name="⚡ Số HĐ điện"
                  stroke="#722ed1"
                  strokeWidth={3}
                  dot={{ fill: '#722ed1', r: 6, stroke: '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 8, stroke: '#722ed1', strokeWidth: 2, fill: '#fff' }}
                />
                <Line
                  yAxisId="bills"
                  type="monotone"
                  dataKey="waterBillCount"
                  name="💧 Số HĐ nước sạch"
                  stroke="#eb2f96"
                  strokeWidth={3}
                  dot={{ fill: '#eb2f96', r: 6, stroke: '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 8, stroke: '#eb2f96', strokeWidth: 2, fill: '#fff' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
            {/* Thống kê tổng quan */}
            <div style={{
              marginTop: 20,
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
              gap: 12
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                color: 'white',
                padding: 12,
                borderRadius: 8,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 12, opacity: 0.9 }}>Tổng tiền điện</div>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                  {revenueData.reduce((sum, item) => sum + (item.electricityRevenue || 0), 0).toLocaleString('vi-VN')} VNĐ
                </div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #52c41a 0%, #b7eb8f 100%)',
                color: 'white',
                padding: 12,
                borderRadius: 8,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 12, opacity: 0.9 }}>Tổng tiền nước sạch</div>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                  {revenueData.reduce((sum, item) => sum + (item.waterRevenue || 0), 0).toLocaleString('vi-VN')} VNĐ
                </div>
              </div>
              {/* tháng cao nhất */}
              <div style={{
                background: 'linear-gradient(135deg, #722ed1 0%, #d3adf7 100%)',
                color: 'white',
                padding: 12,
                borderRadius: 8,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 12, opacity: 0.9 }}>Tổng doanh thu (2025)</div>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                  {revenueData.reduce((sum, item) => sum + (item.electricityRevenue || 0) + (item.waterRevenue || 0), 0).toLocaleString('vi-VN')} VNĐ
                </div>
              </div>

            </div>
          </Card>
        </Col>
      </Row>
      {/* <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title={`Chi tiết doanh thu theo năm ${selectedYear} (Đã thanh toán)`}>
            <Table
              dataSource={monthlyComparisonData.data || []}
              columns={[
                {
                  title: 'Thời gian',
                  dataIndex: 'month',
                  key: 'month',
                  defaultSortOrder: 'ascend',
                  sorter: (a, b) => {
                    const monthA = parseInt(a.month);
                    const monthB = parseInt(b.month);
                    return monthB - monthA;
                  },
                  render: value => `Tháng ${value}`,
                },
                {
                  title: 'Doanh thu thuê (VNĐ)',
                  dataIndex: 'rentRevenue',
                  key: 'rentRevenue',
                  render: (value) => value.toLocaleString('vi-VN'),
                  sorter: (a, b) => a.rentRevenue - b.rentRevenue,
                },
                {
                  title: 'Tiền điện (VNĐ)',
                  dataIndex: 'electricityRevenue',
                  key: 'electricityRevenue',
                  render: (value) => value.toLocaleString('vi-VN'),
                  sorter: (a, b) => a.electricityRevenue - b.electricityRevenue,
                },
                {
                  title: 'Tiền nước sạch (VNĐ)',
                  dataIndex: 'waterRevenue',
                  key: 'waterRevenue',
                  render: (value) => value.toLocaleString('vi-VN'),
                  sorter: (a, b) => a.waterRevenue - b.waterRevenue,
                },
                {
                  title: 'Tổng điện nước (VNĐ)',
                  dataIndex: 'utilityRevenue',
                  key: 'utilityRevenue',
                  render: (value) => value.toLocaleString('vi-VN'),
                  sorter: (a, b) => a.utilityRevenue - b.utilityRevenue,
                },
                {
                  title: 'Tổng doanh thu (VNĐ)',
                  dataIndex: 'totalRevenue',
                  key: 'totalRevenue',
                  render: (value) => value.toLocaleString('vi-VN'),
                  sorter: (a, b) => a.totalRevenue - b.totalRevenue,
                },
                {
                  title: 'Số hợp đồng',
                  dataIndex: 'contractCount',
                  key: 'contractCount',
                  sorter: (a, b) => a.contractCount - b.contractCount,
                },
                {
                  title: 'So với tháng trước (%)',
                  dataIndex: 'growthRate',
                  key: 'growthRate',
                  render: value => {
                    if (value === null) return '-';
                    const numValue = parseFloat(value);
                    const color = numValue > 0 ? '#52c41a' : numValue < 0 ? '#f5222d' : '#faad14';
                    return (
                      <span style={{ color }}>
                        {numValue > 0 ? '+' : ''}{numValue.toFixed(2)}%
                      </span>
                    );
                  },
                  sorter: (a, b) => a.growthRate - b.growthRate,
                },
              ]}
              rowKey="period"
              pagination={false}
              scroll={{ x: 'max-content' }}
              summary={pageData => {
                const totalRevenue = pageData.reduce((total, row) => total + row.rentRevenue, 0) - pageData.reduce((total, row) => total + row.utilityRevenue, 0);
                const totalUtilityRevenue = pageData.reduce((total, row) => total + row.utilityRevenue, 0);
                const totalElectricityRevenue = pageData.reduce((total, row) => total + row.electricityRevenue, 0);
                const totalWaterRevenue = pageData.reduce((total, row) => total + row.waterRevenue, 0);
                const totalTotalRevenue = pageData.reduce((total, row) => total + row.totalRevenue, 0);
                const totalContracts = pageData.reduce((total, row) => total + row.contractCount, 0);
                
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                      <Table.Summary.Cell index={0}>Tổng cộng</Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>{totalRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>{totalElectricityRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>{totalWaterRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>{totalUtilityRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>{totalTotalRevenue.toLocaleString('vi-VN')}</Table.Summary.Cell>
                      <Table.Summary.Cell index={6}>{totalContracts}</Table.Summary.Cell>
                      <Table.Summary.Cell index={7}>-</Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
        </Col>
      </Row> */}
    </>
  );

  const renderTenantTab = () => (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={10}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Chọn khách thuê:</label>
            <Select
              placeholder="Chọn khách thuê"
              style={{ width: '100%' }}
              value={selectedTenant}
              onChange={handleTenantChange}
              showSearch
              allowClear
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes((input ?? '').toLowerCase())
              }
              options={tenants.map(tenant => ({ value: tenant.id, label: tenant.name }))}
            />
          </div>
        </Col>
        <Col xs={24} md={10}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Chọn khoảng thời gian:</label>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              onChange={handleTenantDateRangeChange}
              value={tenantDateRange}
              format="DD/MM/YYYY"
              allowClear
            />
          </div>
        </Col>
        <Col xs={24} md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetchTenantReport}
            style={{ marginBottom: isMobile ? 0 : '1px' }}
          >
            Xem báo cáo
          </Button>
        </Col>
      </Row>
      {/* Hiển thị khoảng thời gian đã chọn */}
      {tenantDateRange && tenantDateRange.length === 2 && (
        <div style={{ marginBottom: 16, textAlign: 'center' }}>
          <h3 style={{ margin: 0 }}>
            Báo cáo từ ngày <strong>{tenantDateRange[0].format('DD/MM/YYYY')}</strong> đến ngày <strong>{tenantDateRange[1].format('DD/MM/YYYY')}</strong>
          </h3>
        </div>
      )}

      {tenantReportData && (
        <>
          <Row gutter={[16, 16]} justify="center">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                bordered={false}
                style={{
                  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                  borderRadius: 20,
                  boxShadow: '0 8px 32px rgba(168, 237, 234, 0.3)',
                  color: '#2E8B57',
                  minHeight: 220,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
                bodyStyle={{ padding: 28 }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🏭</div>
                  <div style={{ fontWeight: 600, fontSize: 16, opacity: 0.9 }}>Tổng tiền thuê & dịch vụ</div>
                  <div style={{ fontSize: 28, fontWeight: 'bold', margin: '8px 0' }}>
                    {(tenantReportData.summary?.totalRentAmount || 0).toLocaleString('vi-VN')} VNĐ
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                bordered={false}
                style={{
                  background: 'linear-gradient(135deg, #722ed1 0%, #b37feb 100%)',
                  borderRadius: 18,
                  boxShadow: '0 4px 24px rgba(114,46,209,0.10)',
                  color: '#fff',
                  minHeight: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
                bodyStyle={{ padding: 28 }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⚡</div>
                  <div style={{ fontWeight: 600, fontSize: 16, opacity: 0.9 }}>Tổng tiền điện</div>
                  <div style={{ fontSize: 28, fontWeight: 'bold', margin: '8px 0' }}>
                    {(tenantReportData.summary?.totalElectricityAmount || 0).toLocaleString('vi-VN')} VNĐ
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                bordered={false}
                style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  borderRadius: 20,
                  boxShadow: '0 4px 24px rgba(240, 147, 251, 0.3)',
                  color: '#fff',
                  minHeight: 220,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
                bodyStyle={{ padding: 28 }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>💧</div>
                  <div style={{ fontWeight: 600, fontSize: 16, opacity: 0.9 }}>Tổng tiền nước sạch</div>
                  <div style={{ fontSize: 28, fontWeight: 'bold', margin: '8px 0', color: '#fff' }}>
                    {(tenantReportData.summary?.totalWaterAmount || 0).toLocaleString('vi-VN')} VNĐ
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                bordered={false}
                style={{
                  background: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
                  borderRadius: 20,
                  boxShadow: '0 4px 24px rgba(132, 250, 176, 0.3)',
                  color: '#fff',
                  minHeight: 220,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
                bodyStyle={{ padding: 28 }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🔧</div>
                  <div style={{ fontWeight: 600, fontSize: 16, opacity: 0.9 }}>Tổng tiền nước thải</div>
                  <div style={{ fontSize: 28, fontWeight: 'bold', margin: '8px 0', color: '#fff' }}>
                    {(tenantReportData.summary?.totalOtherAmount || 0).toLocaleString('vi-VN')} VNĐ
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                bordered={false}
                style={{
                  background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                  borderRadius: 18,
                  boxShadow: '0 4px 24px rgba(210, 105, 30, 0.2)',
                  color: '#D2691E',
                  minHeight: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
                bodyStyle={{ padding: 28 }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>💰</div>
                  <div style={{ fontWeight: 600, fontSize: 16, opacity: 0.9 }}>Tổng chi phí</div>
                  <div style={{ fontSize: 28, fontWeight: 'bold', margin: '8px 0' }}>
                    {(tenantReportData.summary?.totalRevenue || 0).toLocaleString('vi-VN')} VNĐ
                  </div>
                  <div style={{ fontSize: 14, marginTop: 8 }}>
                    <span style={{ color: '#228B22' }}>Đã TT: </span>
                    <span style={{ color: '#D2691E', fontWeight: 600 }}>
                      {((tenantReportData.summary?.totalRevenue || 0) - (tenantReportData.summary?.totalDebt || 0)).toLocaleString('vi-VN')} VNĐ
                    </span>
                    <br />
                    <span style={{ color: '#DC143C' }}>Chưa TT: </span>
                    <span style={{ color: '#D2691E', fontWeight: 600 }}>
                      {(tenantReportData.summary?.totalDebt || 0).toLocaleString('vi-VN')} VNĐ
                    </span>
                    <br />
                    <span style={{ color: '#FF8C00' }}>Tỷ lệ: </span>
                    <span style={{ color: '#D2691E', fontWeight: 600 }}>
                      {(() => {
                        const total = tenantReportData.summary?.totalRevenue || 0;
                        const paid = total - (tenantReportData.summary?.totalDebt || 0);
                        return total > 0 ? ((paid / total) * 100).toFixed(0) : 0;
                      })()}%
                    </span>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 22 }}>📊</span>
                    <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: 18 }}>
                      Biểu đồ chi tiêu theo tháng - {tenantReportData.tenantName}
                    </span>
                  </div>
                }
                bordered={false}
                bodyStyle={{
                  padding: '20px',
                  background: 'linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%)',
                  borderRadius: '12px'
                }}
                style={{
                  boxShadow: '0 8px 32px rgba(24,144,255,0.1)',
                  borderRadius: '12px'
                }}
              >
                <ResponsiveContainer width="99%" height={isMobile ? 300 : 400}>
                  <BarChart
                    data={tenantReportData.monthlyData || []}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 20,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="month"
                      label={{
                        value: 'Tháng',
                        position: 'bottom',
                        style: { textAnchor: 'middle', fill: '#666', fontSize: '14px', fontWeight: 'bold' }
                      }}
                      tick={{ fontSize: 12, fill: '#333' }}
                      tickLine={{ stroke: '#666' }}
                    />
                    <YAxis
                      yAxisId="amount"
                      orientation="left"
                      label={{
                        value: 'Số tiền (VNĐ)',
                        angle: -90,
                        position: 'insideLeft',
                        style: { textAnchor: 'middle', fill: '#1890ff', fontSize: '14px', fontWeight: 'bold' }
                      }}
                      tickFormatter={(value) => `${(value / 1000000).toFixed(0)}tr`}
                      tick={{ fontSize: 12, fill: '#1890ff' }}
                      tickLine={{ stroke: '#1890ff' }}
                    />

                    <RechartsTooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const rentAmount = data.rentAmount || 0;
                          const electricityAmount = data.electricityAmount || 0;
                          const waterAmount = data.waterAmount || 0;
                          const otherAmount = data.otherAmount || 0;
                          const rentPaid = data.rentPaid || 0;

                          return (
                            <div style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.98)',
                              padding: '16px',
                              border: '2px solid #1890ff',
                              borderRadius: '12px',
                              boxShadow: '0 8px 32px rgba(24,144,255,0.15)',
                              backdropFilter: 'blur(10px)',
                              minWidth: '320px'
                            }}>
                              <div style={{
                                background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
                                color: 'white',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                marginBottom: '12px',
                                textAlign: 'center',
                                fontWeight: 'bold'
                              }}>
                                📅 Tháng {label}
                              </div>

                              <div style={{ marginBottom: '12px' }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '8px',
                                  padding: '8px',
                                  background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                                  borderRadius: '6px',
                                  color: 'white'
                                }}>
                                  <span style={{ fontWeight: 'bold' }}>🏭 Tiền thuê:</span>
                                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                    {rentAmount.toLocaleString('vi-VN')} VNĐ
                                  </span>
                                </div>
                              </div>

                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr',
                                gap: '8px',
                                marginBottom: '12px'
                              }}>
                                <div style={{
                                  padding: '8px',
                                  background: 'rgba(24, 144, 255, 0.1)',
                                  borderRadius: '6px',
                                  border: '1px solid #1890ff'
                                }}>
                                  <div style={{ color: '#1890ff', fontSize: '12px', fontWeight: 'bold' }}>⚡ Tiền điện</div>
                                  <div style={{ color: '#333', fontSize: '14px', fontWeight: '600' }}>
                                    {electricityAmount.toLocaleString('vi-VN')} VNĐ
                                  </div>
                                </div>

                                <div style={{
                                  padding: '8px',
                                  background: 'rgba(82, 196, 26, 0.1)',
                                  borderRadius: '6px',
                                  border: '1px solid #52c41a'
                                }}>
                                  <div style={{ color: '#52c41a', fontSize: '12px', fontWeight: 'bold' }}>💧 Tiền nước sạch</div>
                                  <div style={{ color: '#333', fontSize: '14px', fontWeight: '600' }}>
                                    {waterAmount.toLocaleString('vi-VN')} VNĐ
                                  </div>
                                </div>

                                <div style={{
                                  padding: '8px',
                                  background: 'rgba(114, 46, 209, 0.1)',
                                  borderRadius: '6px',
                                  border: '1px solid #722ed1'
                                }}>
                                  <div style={{ color: '#722ed1', fontSize: '12px', fontWeight: 'bold' }}>🔧 Tiền nước thải</div>
                                  <div style={{ color: '#333', fontSize: '14px', fontWeight: '600' }}>
                                    {otherAmount.toLocaleString('vi-VN')} VNĐ
                                  </div>
                                </div>
                              </div>

                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                background: 'linear-gradient(135deg, #722ed1 0%, #eb2f96 100%)',
                                borderRadius: '6px',
                                color: 'white'
                              }}>
                                <span style={{ fontWeight: 'bold' }}>💰 Tổng chi phí:</span>
                                <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                  {(rentAmount + electricityAmount + waterAmount + otherAmount).toLocaleString('vi-VN')} VNĐ
                                </span>
                              </div>

                              {rentPaid > 0 && (
                                <div style={{
                                  marginTop: '8px',
                                  padding: '6px',
                                  background: 'rgba(255, 193, 7, 0.1)',
                                  borderRadius: '4px',
                                  border: '1px solid #ffc107',
                                  textAlign: 'center'
                                }}>
                                  <span style={{ color: '#856404', fontSize: '12px' }}>
                                    💳 Đã thanh toán thuê: {rentPaid.toLocaleString('vi-VN')} VNĐ
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend
                      verticalAlign="top"
                      height={50}
                      wrapperStyle={{
                        paddingBottom: '10px'
                      }}
                    />

                    <Bar
                      yAxisId="amount"
                      dataKey="rentAmount"
                      name="🏭 Tiền thuê"
                      fill="#1890ff"
                      maxBarSize={40}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      yAxisId="amount"
                      dataKey="electricityAmount"
                      name="⚡ Tiền điện"
                      fill="#fa8c16"
                      maxBarSize={40}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      yAxisId="amount"
                      dataKey="waterAmount"
                      name="💧 Tiền nước sạch"
                      fill="#52c41a"
                      maxBarSize={40}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      yAxisId="amount"
                      dataKey="otherAmount"
                      name="🔧 Tiền nước thải"
                      fill="#722ed1"
                      maxBarSize={40}
                      radius={[4, 4, 0, 0]}
                    />

                  </BarChart>
                </ResponsiveContainer>

                {/* Thống kê tổng quan */}
                <div style={{
                  marginTop: '20px',
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(6, 1fr)',
                  gap: '12px'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>Tổng tiền thuê & dịch vụ</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      {(tenantReportData.monthlyData || []).reduce((sum, item) => sum + (item.rentAmount || 0), 0).toLocaleString('vi-VN')} VNĐ
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>Tổng tiền điện</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      {(tenantReportData.monthlyData || []).reduce((sum, item) => sum + (item.electricityAmount || 0), 0).toLocaleString('vi-VN')} VNĐ
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>Tổng tiền nước sạch</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      {(tenantReportData.monthlyData || []).reduce((sum, item) => sum + (item.waterAmount || 0), 0).toLocaleString('vi-VN')} VNĐ
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #13c2c2 0%, #006d75 100%)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>Tổng tiền nước thải</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      {(tenantReportData.monthlyData || []).reduce((sum, item) => sum + (item.otherAmount || 0), 0).toLocaleString('vi-VN')} VNĐ
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>Tổng chi phí</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      {(tenantReportData.monthlyData || []).reduce((sum, item) =>
                        sum + (item.rentAmount || 0) + (item.electricityAmount || 0) + (item.waterAmount || 0) + (item.otherAmount || 0), 0
                      ).toLocaleString('vi-VN')} VNĐ
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #13c2c2 0%, #b5f5ec 100%)',
                    color: '#08979c',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '1px solid #b5f5ec'
                  }}>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>Tháng cao nhất</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      {tenantReportData.monthlyData && tenantReportData.monthlyData.length > 0 ?
                        `Tháng ${tenantReportData.monthlyData.reduce((max, item) =>
                          (item.rentAmount + item.electricityAmount + item.waterAmount) >
                            (max.rentAmount + max.electricityAmount + max.waterAmount) ? item : max
                        ).month}` : 'N/A'
                      }
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card
                title="Danh sách nhà xưởng và hợp đồng"
                bordered={false}
              >
                <Table
                  columns={[
                    {
                      title: 'Mã hợp đồng',
                      dataIndex: 'ContractID',
                      key: 'ContractID',
                    },
                    {
                      title: 'Tên nhà xưởng',
                      dataIndex: 'FactoryName',
                      key: 'FactoryName',
                    },
                    {
                      title: 'Tiền thuê (VNĐ)',
                      dataIndex: 'totalRentAmount',
                      key: 'totalRentAmount',
                      render: value => (value ? value.toLocaleString('vi-VN') : '0'),
                    },
                    {
                      title: 'Tiền điện (VNĐ)',
                      dataIndex: 'totalElectricityAmount',
                      key: 'totalElectricityAmount',
                      render: value => (value ? value.toLocaleString('vi-VN') : '0'),
                    },
                    {
                      title: 'Tiền nước sạch(VNĐ)',
                      dataIndex: 'totalWaterAmount',
                      key: 'totalWaterAmount',
                      render: value => (value ? value.toLocaleString('vi-VN') : '0'),
                    },
                    {
                      title: 'Số hóa đơn điện',
                      dataIndex: 'electricityBillCount',
                      key: 'electricityBillCount',
                    },
                    {
                      title: 'Số hóa đơn nước',
                      dataIndex: 'waterBillCount',
                      key: 'waterBillCount',
                    },

                  ]}
                  dataSource={tenantReportData.details || []}
                  rowKey="ContractID"
                  pagination={{
                    pageSize: isMobile ? 5 : isTablet ? 8 : 10,
                    showSizeChanger: !isMobile,
                    showQuickJumper: !isMobile,
                  }}
                  size={isMobile ? 'small' : 'middle'}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );

  const renderInvoiceTab = () => {
    const totalAmount = data.reduce((sum, record) => {
      const amount = parseFloat(record.amount) || 0;
      return sum + amount;
    }, 0);

    const invoiceTypes = [
      { value: 'all', label: 'Tất cả hóa đơn' },
      { value: 'rent', label: 'Hóa đơn thuê' },
      { value: 'electricity', label: 'Hóa đơn điện' },
      { value: 'water', label: 'Hóa đơn nước sạch' }
    ];

    const filteredData = data.filter(invoice =>
      selectedInvoiceType === 'all' ? true : invoice.type === selectedInvoiceType
    );

    const filteredTotalAmount = filteredData.reduce((sum, record) => {
      const amount = parseFloat(record.amount) || 0;
      return sum + amount;
    }, 0);

    const filteredTotalPaid = filteredData.reduce((sum, record) => {
      const paid = parseFloat(record.paidAmount) || 0;
      return sum + paid;
    }, 0);

    const filteredTotalRemaining = filteredData.reduce((sum, record) => {
      const amount = parseFloat(record.amount) || 0;
      const paid = parseFloat(record.paidAmount) || 0;
      return sum + (amount - paid);
    }, 0);

    return (
      <Card
        title={`Danh sách hóa đơn (${filteredData.length} hóa đơn)`}
        style={{ margin: '16px' }}
      >
        <Form form={form} onFinish={handleSearch} style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="status" label="Trạng thái">
                <Select
                  allowClear
                  placeholder="Chọn trạng thái"
                  options={[
                    { value: 'pending', label: 'Chờ thanh toán' },
                    { value: 'paid', label: 'Đã thanh toán' },
                    { value: 'overdue', label: 'Quá hạn' },
                    { value: 'partial', label: 'Chưa thanh toán đủ' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Loại hóa đơn">
                <Select
                  value={selectedInvoiceType}
                  onChange={setSelectedInvoiceType}
                  options={invoiceTypes}
                  placeholder="Chọn loại hóa đơn"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="startDate" label="Từ ngày">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="endDate" label="Đến ngày">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>

            <Col span={10}>
              <Form.Item name="tenantID" label="Khách hàng">
                <Select
                  placeholder="Chọn khách thuê"
                  style={{ width: '100%' }}
                  value={selectedTenant}
                  onChange={handleTenantChange}
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes((input ?? '').toLowerCase())
                  }
                  options={tenants.map(tenant => ({ value: tenant.id, label: tenant.name }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Space>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'brown', marginRight: '10px' }}>
                    Tổng tiền: {new Intl.NumberFormat('vi-VN').format(filteredTotalAmount)} VNĐ
                  </span>
                </div>
                <Button type="primary" icon={<SearchOutlined />} htmlType="submit">
                  Tìm kiếm
                </Button>
                <Button icon={<ReloadOutlined />} onClick={() => {
                  form.resetFields();
                  setSelectedInvoiceType('all');
                  const emptyFilters = {};
                  setFilters(emptyFilters);
                  fetchAllInvoices({
                    current: 1,
                    pageSize: 1000,
                    filters: emptyFilters
                  });
                }}>
                  Làm mới
                </Button>
                <Button
                  type="primary"
                  icon={<ExportOutlined />}
                  onClick={handleExportExcel}
                  loading={loading}
                >
                  Xuất excel
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>

        <Table
          dataSource={filteredData}
          columns={columns}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
          pagination={{
            ...pagination,
            total: filteredData.length,
            pageSize: 10
          }}
          summary={() => {
            const fmt = (n) => new Intl.NumberFormat('vi-VN').format(n || 0);

            // columns: Mã HĐ | Xưởng | Khách thuê | Loại | Kỳ | Số tiền | Đã thanh toán | Còn thiếu | Hạn | Trạng thái
            return (
              <Table.Summary fixed="bottom">
                <Table.Summary.Row style={{ fontWeight: 700, backgroundColor: '#f0f2f5' }}>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    Tổng (theo danh sách đang hiển thị)
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="left" style={{ textAlign: 'left' }}>
                    {fmt(filteredTotalAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="left" style={{ textAlign: 'left' }}>
                    {fmt(filteredTotalPaid)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="left" style={{ textAlign: 'left' }}>
                    {fmt(filteredTotalRemaining)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} colSpan={2} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>
    );
  };

  const fetchMonthlyComparisonData = async () => {
    try {
      setLoading(true);
      let startDate, endDate;

      if (comparisonDateRange && comparisonDateRange.length === 2) {
        startDate = comparisonDateRange[0].format('YYYY-MM-DD');
        endDate = comparisonDateRange[1].format('YYYY-MM-DD');
      } else {
        // Mặc định lấy dữ liệu của năm hiện tại
        startDate = `2022-01-01`;
        endDate = `${selectedYear}-12-31`;
      }

      const response = await fetch(REPORT_API.REVENUE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate,
          endDate,
          groupBy: 'month'
        }),
      });

      const result = await response.json();

      if (result.data && result.data.revenue) {
        // Tính toán tỷ lệ tăng trưởng
        const revenueData = result.data.revenue.map((item, index, array) => {
          let growthRate = 0;
          if (index > 0) {
            const previousRevenue = array[index - 1].totalRevenue;
            growthRate = previousRevenue !== 0
              ? ((item.totalRevenue - previousRevenue) / previousRevenue) * 100
              : 0;
          }
          return {
            ...item,
            growthRate
          };
        });

        // Tính toán thống kê tổng quan
        const summary = {
          totalRevenue: revenueData.reduce((sum, item) => sum + item.totalRevenue, 0),
          averageRevenue: revenueData.reduce((sum, item) => sum + item.totalRevenue, 0) / revenueData.length,
          maxRevenue: Math.max(...revenueData.map(item => item.totalRevenue)),
          minRevenue: Math.min(...revenueData.map(item => item.totalRevenue)),
          maxGrowth: Math.max(...revenueData.map(item => item.growthRate)),
          minGrowth: Math.min(...revenueData.map(item => item.growthRate)),
          averageGrowth: revenueData.reduce((sum, item) => sum + item.growthRate, 0) / (revenueData.length - 1)
        };

        setMonthlyComparisonData({
          data: revenueData,
          summary
        });
      }
    } catch (error) {
      console.error('Error fetching monthly comparison data:', error);
      message.error('Không thể tải dữ liệu so sánh doanh thu theo tháng');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyComparisonData();
  }, [comparisonDateRange]);

  const renderMonthlyComparisonTab = () => (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="Thống kê doanh thu theo thời gian" extra={
            <DatePicker.RangePicker
              onChange={(dates) => {
                if (dates) {
                  fetchRevenueComparison(dates[0].toDate(), dates[1].toDate());
                }
              }}
              format="DD/MM/YYYY"
            />
          }>
            <Table
              loading={loading}
              dataSource={revenueComparisonData.data}
              columns={[
                {
                  title: 'Thời gian',
                  dataIndex: 'period',
                  key: 'period',
                  fixed: 'left',
                  width: 100
                },
                {
                  title: 'Doanh thu thuê',
                  children: [
                    {
                      title: 'Số tiền (VNĐ)',
                      dataIndex: 'rentRevenue',
                      key: 'rentRevenue',
                      render: value => formatRevenue(value),
                      width: 150
                    },
                    {
                      title: 'Số HĐ',
                      dataIndex: ['billCount', 'rent'],
                      key: 'rentBillCount',
                      width: 80
                    }
                  ]
                },
                {
                  title: 'Tiền điện',
                  children: [
                    {
                      title: 'Số tiền (VNĐ)',
                      dataIndex: 'electricityRevenue',
                      key: 'electricityRevenue',
                      render: value => formatRevenue(value),
                      width: 150
                    },
                    {
                      title: 'Số HĐ',
                      dataIndex: ['billCount', 'electricity'],
                      key: 'electricityBillCount',
                      width: 80
                    }
                  ]
                },
                {
                  title: 'Tiền nước sạch',
                  children: [
                    {
                      title: 'Số tiền (VNĐ)',
                      dataIndex: 'waterRevenue',
                      key: 'waterRevenue',
                      render: value => formatRevenue(value),
                      width: 150
                    },
                    {
                      title: 'Số HĐ',
                      dataIndex: ['billCount', 'water'],
                      key: 'waterBillCount',
                      width: 80
                    }
                  ]
                },
                {
                  title: 'Tiền dịch vụ',
                  children: [
                    {
                      title: 'Số tiền (VNĐ)',
                      dataIndex: 'serviceRevenue',
                      key: 'serviceRevenue',
                      render: value => formatRevenue(value),
                      width: 150
                    },
                    {
                      title: 'Số HĐ',
                      dataIndex: ['billCount', 'service'],
                      key: 'serviceBillCount',
                      width: 80
                    }
                  ]
                },
                {
                  title: 'Tiền nước thải',
                  children: [
                    {
                      title: 'Số tiền (VNĐ)',
                      dataIndex: 'otherRevenue',
                      key: 'otherRevenue',
                      render: value => formatRevenue(value),
                      width: 150
                    },
                    {
                      title: 'Số HĐ',
                      dataIndex: ['billCount', 'other'],
                      key: 'otherBillCount',
                      width: 80
                    }
                  ]
                },
                {
                  title: 'Trạng thái thanh toán',
                  children: [
                    {
                      title: 'Đã TT (VNĐ)',
                      dataIndex: 'paidRevenue',
                      key: 'paidRevenue',
                      render: value => formatRevenue(value),
                      width: 150
                    },
                    {
                      title: 'Chưa TT (VNĐ)',
                      dataIndex: 'unpaidRevenue',
                      key: 'unpaidRevenue',
                      render: value => formatRevenue(value),
                      width: 150
                    },
                    {
                      title: 'Quá hạn (VNĐ)',
                      dataIndex: 'overdueRevenue',
                      key: 'overdueRevenue',
                      render: value => formatRevenue(value),
                      width: 150
                    }
                  ]
                },
                {
                  title: 'Tổng doanh thu (VNĐ)',
                  dataIndex: 'totalRevenue',
                  key: 'totalRevenue',
                  fixed: 'right',
                  render: value => formatRevenue(value),
                  width: 150
                },
                {
                  title: 'Tăng trưởng',
                  dataIndex: 'growthRate',
                  key: 'growthRate',
                  fixed: 'right',
                  render: value => {
                    if (!value && value !== 0) return '-';
                    const color = value > 0 ? '#52c41a' : value < 0 ? '#f5222d' : '#faad14';
                    return <span style={{ color }}>{value > 0 ? '+' : ''}{value}%</span>;
                  },
                  width: 100
                }
              ]}
              scroll={{ x: 'max-content' }}
              pagination={false}
              summary={pageData => {
                const totals = pageData.reduce((acc, curr) => ({
                  rentRevenue: acc.rentRevenue + (curr.rentRevenue || 0),
                  electricityRevenue: acc.electricityRevenue + (curr.electricityRevenue || 0),
                  waterRevenue: acc.waterRevenue + (curr.waterRevenue || 0),
                  serviceRevenue: acc.serviceRevenue + (curr.serviceRevenue || 0),
                  otherRevenue: acc.otherRevenue + (curr.otherRevenue || 0),
                  paidRevenue: acc.paidRevenue + (curr.paidRevenue || 0),
                  unpaidRevenue: acc.unpaidRevenue + (curr.unpaidRevenue || 0) + (curr.partialRevenue || 0),
                  overdueRevenue: acc.overdueRevenue + (curr.overdueRevenue || 0),
                  totalRevenue: acc.totalRevenue + (curr.totalRevenue || 0),
                  billCount: {
                    rent: acc.billCount.rent + ((curr.billCount && curr.billCount.rent) || 0),
                    electricity: acc.billCount.electricity + ((curr.billCount && curr.billCount.electricity) || 0),
                    water: acc.billCount.water + ((curr.billCount && curr.billCount.water) || 0),
                    service: acc.billCount.service + ((curr.billCount && curr.billCount.service) || 0),
                    other: acc.billCount.other + ((curr.billCount && curr.billCount.other) || 0)
                  }
                }), {
                  rentRevenue: 0,
                  electricityRevenue: 0,
                  waterRevenue: 0,
                  serviceRevenue: 0,
                  otherRevenue: 0,
                  paidRevenue: 0,
                  unpaidRevenue: 0,
                  overdueRevenue: 0,
                  totalRevenue: 0,
                  billCount: {
                    rent: 0,
                    electricity: 0,
                    water: 0,
                    service: 0,
                    other: 0
                  }
                });

                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                      <Table.Summary.Cell index={0} fixed="left">Tổng cộng</Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>{formatRevenue(totals.rentRevenue)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>{totals.billCount.rent}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>{formatRevenue(totals.electricityRevenue)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>{totals.billCount.electricity}</Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>{formatRevenue(totals.waterRevenue)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={6}>{totals.billCount.water}</Table.Summary.Cell>
                      <Table.Summary.Cell index={7}>{formatRevenue(totals.serviceRevenue)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={8}>{totals.billCount.service}</Table.Summary.Cell>
                      <Table.Summary.Cell index={9}>{formatRevenue(totals.otherRevenue)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={10}>{totals.billCount.other}</Table.Summary.Cell>
                      <Table.Summary.Cell index={11}>{formatRevenue(totals.paidRevenue)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={12}>{formatRevenue(totals.unpaidRevenue)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={13}>{formatRevenue(totals.overdueRevenue)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={14} fixed="right">{formatRevenue(totals.totalRevenue)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={15} fixed="right">-</Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
        </Col>
      </Row>
    </>
  )

  const fetchRevenueComparison = async (startDate, endDate) => {
    try {
      setLoading(true);
      const response = await axios.post(REPORT_API.REVENUE_COMPARISON, {
        startDate,
        endDate
      });

      if (response.data?.data) {
        // Group data by month/year
        const groupedData = _.groupBy(response.data.data, item => `${item.Year}-${item.Month}`);

        // Transform data for table display
        const tableData = Object.entries(groupedData).map(([period, items]) => {
          const row = {
            key: period,
            period: moment(`${period}-01`).format('MM/YYYY'),
            rentRevenue: 0,
            electricityRevenue: 0,
            waterRevenue: 0,
            serviceRevenue: 0,
            otherRevenue: 0,
            paidRevenue: 0,
            unpaidRevenue: 0,
            overdueRevenue: 0,
            partialRevenue: 0,
            billCount: {
              rent: 0,
              electricity: 0,
              water: 0,
              service: 0,
              other: 0
            },
            totalRevenue: 0
          };
          console.log(items);

          items.forEach(item => {
            // Sum revenues by type
            const amount = item.Amount || 0;
            const paidAmount = item.PaidAmount || 0;
            switch (item.Type) {
              case 'rent':
                row.rentRevenue += amount;
                row.billCount.rent += item.ContractCount || 0;
                break;
              case 'electricity':
                row.electricityRevenue += amount;
                row.billCount.electricity += item.ContractCount || 0;
                break;
              case 'water':
                row.waterRevenue += amount;
                row.billCount.water += item.ContractCount || 0;
                break;
              case 'service':
                row.serviceRevenue += amount;
                row.billCount.service += item.ContractCount || 0;
                break;
              case 'other':
                row.otherRevenue += amount;
                row.billCount.other += item.ContractCount || 0;
                break;
            }

            // Sum by payment status
            switch (item.Status) {
              case 'paid':
                row.paidRevenue += amount;
                break;
              case 'unpaid':
              case 'pending':
                row.unpaidRevenue += amount;
                break;
              case 'partial':
                row.unpaidRevenue += amount - paidAmount;
                row.paidRevenue += paidAmount;
                break;
              case 'overdue':
                row.overdueRevenue += amount;
                break;
            }
          });

          row.totalRevenue = row.rentRevenue + row.electricityRevenue +
            row.waterRevenue + row.serviceRevenue + row.otherRevenue;

          return row;
        });

        // Sort by period descending
        const sortedData = _.orderBy(tableData, [
          row => moment(row.period, 'MM/YYYY').valueOf()
        ], ['desc']);

        // Calculate growth rates
        sortedData.forEach((row, index) => {
          if (index < sortedData.length - 1) {
            const currentRevenue = row.totalRevenue;
            const previousRevenue = sortedData[index + 1].totalRevenue;
            row.growthRate = previousRevenue ?
              ((currentRevenue - previousRevenue) / previousRevenue * 100).toFixed(2) : 0;
          }
        });

        setRevenueComparisonData({
          data: sortedData
        });
      }
    } catch (error) {
      console.error('Error fetching revenue comparison:', error);
      message.error('Không thể tải dữ liệu so sánh doanh thu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Mặc định lấy dữ liệu 6 tháng gần nhất
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 36);

    fetchRevenueComparison(startDate, endDate);
    fetchFactoryGroupReport();
  }, []);

  const fetchFactoryGroupReport = async () => {
    try {
      setFactoryGroupLoading(true);
      let startDate, endDate;

      if (factoryGroupDateRange && factoryGroupDateRange.length === 2) {
        startDate = factoryGroupDateRange[0].format('YYYY-MM-DD');
        endDate = factoryGroupDateRange[1].format('YYYY-MM-DD');
      } else {
        // Mặc định lấy dữ liệu của năm hiện tại
        startDate = `2022-01-01`;
        endDate = `${selectedYear}-12-31`;
      }

      // Gọi API lấy dữ liệu
      const response = await axios.get(REPORT_API.FACTORY_GROUP, {
        params: {
          startDate,
          endDate
        }
      });
      const details = response.data.data.details || [];
      const mappedDetails = details.map(item => {
        const occupiedArea = (item.area * item.occupancyRate) / 100;
        return {
          ...item,
          occupiedArea,
          emptyArea: item.area - occupiedArea,
          totalRevenue: item.totalRevenue ?? item.revenue ?? 0,
          paidRevenue: item.paidRevenue ?? 0,
          unpaidRevenue: item.unpaidRevenue ?? 0,
          overdueRevenue: item.overdueRevenue ?? 0,
          paymentRate: item.paymentRate ?? 0,
          paidBillCount: item.paidBillCount ?? 0,
          unpaidBillCount: item.unpaidBillCount ?? 0,
          overdueBillCount: item.overdueBillCount ?? 0,
        };
      });

      setFactoryGroupData(mappedDetails);
    } catch (error) {
      console.error('Lỗi khi tải báo cáo nhóm nhà xưởng:', error);
      message.error('Không thể tải báo cáo nhóm nhà xưởng');
    } finally {
      setFactoryGroupLoading(false);
    }
  };

  const renderFactoryGroupTab = () => {
    const columns = [
      {
        title: 'Nhóm nhà xưởng',
        dataIndex: 'group',
        key: 'group',
        fixed: 'left',
      },
      {
        title: 'Số lượng',
        dataIndex: 'factoryCount',
        key: 'factoryCount',
        render: value => new Intl.NumberFormat('vi-VN').format(value),
      },
      {
        title: 'Diện tích',
        children: [
          {
            title: 'Tổng diện tích (m²)',
            dataIndex: 'area',
            key: 'area',
            render: value => new Intl.NumberFormat('vi-VN').format(value),
          },
          {
            title: 'Đã cho thuê (m²)',
            dataIndex: 'occupiedArea',
            key: 'occupiedArea',
            render: value => new Intl.NumberFormat('vi-VN').format(value),
          },
          {
            title: 'Còn trống (m²)',
            dataIndex: 'emptyArea',
            key: 'emptyArea',
            render: value => new Intl.NumberFormat('vi-VN').format(value),
          },
          {
            title: 'Tỷ lệ lấp đầy (%)',
            dataIndex: 'occupancyRate',
            key: 'occupancyRate',
            render: value => `${Number(value).toFixed(2)}%`,
          }
        ]
      },
      {
        title: 'Doanh thu',
        children: [
          {
            title: 'Tổng doanh thu (VNĐ)',
            dataIndex: 'totalRevenue',
            key: 'totalRevenue',
            render: value => new Intl.NumberFormat('vi-VN').format(value),
          },
          {
            title: 'Đã thu (VNĐ)',
            dataIndex: 'paidRevenue',
            key: 'paidRevenue',
            render: value => new Intl.NumberFormat('vi-VN').format(value),
          },
          {
            title: 'Còn nợ (VNĐ)',
            dataIndex: 'unpaidRevenue',
            key: 'unpaidRevenue',
            render: (value, record) => new Intl.NumberFormat('vi-VN').format(value + (record.partialRevenue || 0)),
          },
          {
            title: 'Quá hạn (VNĐ)',
            dataIndex: 'overdueRevenue',
            key: 'overdueRevenue',
            render: value => new Intl.NumberFormat('vi-VN').format(value),
          },
          {
            title: 'Tỷ lệ thu (%)',
            dataIndex: 'paymentRate',
            key: 'paymentRate',
            render: value => `${Number(value).toFixed(2)}%`,
          }
        ]
      },
      {
        title: 'Số lượng hóa đơn',
        children: [
          {
            title: 'Đã thanh toán',
            dataIndex: 'paidBillCount',
            key: 'paidBillCount',
            render: value => new Intl.NumberFormat('vi-VN').format(value),
          },
          {
            title: 'Chưa thanh toán',
            dataIndex: 'unpaidBillCount',
            key: 'unpaidBillCount',
            render: value => new Intl.NumberFormat('vi-VN').format(value),
          },
          {
            title: 'Quá hạn',
            dataIndex: 'overdueBillCount',
            key: 'overdueBillCount',
            render: value => new Intl.NumberFormat('vi-VN').format(value),
          }
        ]
      },
      {
        title: 'Thao tác',
        key: 'action',
        fixed: 'right',
        render: (_, record) => (
          <Button
            type="link"
            size="small"
            onClick={() => {
              setFactoryGroupDetail(record);
              setFactoryGroupInvoiceType('all');
              setFactoryGroupInvoiceStatus('all');
            }}
          >
            Xem hóa đơn
          </Button>
        )
      }
    ];

    const filteredData = (selectedFactoryGroup
      ? factoryGroupData.filter(item => item.group === selectedFactoryGroup)
      : [...factoryGroupData]).sort((a, b) => {
        if (a.group?.includes('11-12') && !b.group?.includes('11-12')) return 1;
        if (!a.group?.includes('11-12') && b.group?.includes('11-12')) return -1;
        return (a.group || '').localeCompare(b.group || '', undefined, { numeric: true, sensitivity: 'base' });
      });

    return (
      <Card
        title="Báo cáo theo nhóm nhà xưởng"
        style={{ margin: '16px' }}
      >
        <Space direction="vertical" style={{ width: '100%', marginBottom: '16px' }}>
          <Space wrap>
            <DatePicker.RangePicker
              value={factoryGroupDateRange}
              onChange={setFactoryGroupDateRange}
              format="DD/MM/YYYY"
            />
            <Button type="primary" onClick={fetchFactoryGroupReport}>
              Xem báo cáo
            </Button>
            <Select
              allowClear
              placeholder="Lọc theo nhóm"
              style={{ minWidth: 200 }}
              value={selectedFactoryGroup}
              onChange={setSelectedFactoryGroup}
              options={Array.from(new Set(factoryGroupData.map(item => item.group))).map(group => ({
                value: group,
                label: group
              }))}
            />
          </Space>
        </Space>

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={factoryGroupLoading}
          pagination={false}
          bordered
          size="middle"
          scroll={{ x: 2000 }}
          summary={pageData => {
            const totals = pageData.reduce((acc, record) => {
              acc.factoryCount += record.factoryCount || 0;
              acc.area += record.area || 0;
              acc.occupiedArea += record.occupiedArea || 0;
              acc.emptyArea += record.emptyArea || 0;
              acc.totalRevenue += record.totalRevenue || 0;
              acc.paidRevenue += record.paidRevenue || 0;
              acc.unpaidRevenue += (record.unpaidRevenue || 0) + (record.partialRevenue || 0);
              acc.overdueRevenue += record.overdueRevenue || 0;
              acc.paidBillCount += record.paidBillCount || 0;
              acc.unpaidBillCount += record.unpaidBillCount || 0;
              acc.overdueBillCount += record.overdueBillCount || 0;
              return acc;
            }, {
              factoryCount: 0,
              area: 0,
              occupiedArea: 0,
              emptyArea: 0,
              totalRevenue: 0,
              paidRevenue: 0,
              unpaidRevenue: 0,
              overdueRevenue: 0,
              paidBillCount: 0,
              unpaidBillCount: 0,
              overdueBillCount: 0
            });

            const totalOccupancyRate = totals.area > 0 ? ((totals.occupiedArea / totals.area) * 100).toFixed(2) : '0.00';
            const totalPaymentRate = totals.totalRevenue > 0 ? ((totals.paidRevenue / totals.totalRevenue) * 100).toFixed(2) : '0.00';

            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                  <Table.Summary.Cell index={0}>Tổng cộng</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>{new Intl.NumberFormat('vi-VN').format(totals.factoryCount)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>{new Intl.NumberFormat('vi-VN').format(totals.area)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>{new Intl.NumberFormat('vi-VN').format(totals.occupiedArea)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>{new Intl.NumberFormat('vi-VN').format(totals.emptyArea)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>{`${totalOccupancyRate}%`}</Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>{new Intl.NumberFormat('vi-VN').format(totals.totalRevenue)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={7}>{new Intl.NumberFormat('vi-VN').format(totals.paidRevenue)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={8}>{new Intl.NumberFormat('vi-VN').format(totals.unpaidRevenue)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={9}>{new Intl.NumberFormat('vi-VN').format(totals.overdueRevenue)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={11}>{`${totalPaymentRate}%`}</Table.Summary.Cell>
                  <Table.Summary.Cell index={12}>{new Intl.NumberFormat('vi-VN').format(totals.paidBillCount)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={13}>{new Intl.NumberFormat('vi-VN').format(totals.unpaidBillCount)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={14}>{new Intl.NumberFormat('vi-VN').format(totals.overdueBillCount)}</Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />

        {factoryGroupDetail && (() => {
          const invoices = factoryGroupDetail.invoices || [];
          const filteredInvoices = invoices.filter(inv => {
            const matchType =
              factoryGroupInvoiceType === 'all' || inv.Type === factoryGroupInvoiceType;
            const matchStatus =
              factoryGroupInvoiceStatus === 'all' || inv.Status === factoryGroupInvoiceStatus;
            return matchType && matchStatus;
          });

          const totalAmount = invoices.reduce((sum, inv) => sum + (inv.Amount || 0), 0);
          const paidAmount = invoices.reduce((sum, inv) => sum + (inv.PaidAmount || 0), 0);
          const overdueAmount = invoices
            .filter(inv => inv.Status === 'overdue')
            .reduce((sum, inv) => sum + (inv.Amount || 0), 0);
          const remainingAmount = totalAmount - paidAmount;
          const paymentRate = totalAmount ? (paidAmount / totalAmount) * 100 : 0;

          const typeOptions = Array.from(new Set(invoices.map(inv => inv.Type))).map(t => ({
            value: t,
            label:
              t === 'rent'
                ? 'Tiền thuê'
                : t === 'electricity'
                  ? 'Tiền điện'
                  : t === 'water'
                    ? 'Tiền nước sạch'
                    : t === 'service'
                      ? 'Dịch vụ'
                      : t,
          }));

          const statusOptions = Array.from(new Set(invoices.map(inv => inv.Status))).map(s => ({
            value: s,
            label:
              s === 'paid'
                ? 'Đã thanh toán'
                : s === 'overdue'
                  ? 'Quá hạn'
                  : s === 'pending' || s === 'unpaid'
                    ? 'Chưa thanh toán'
                    : s === 'partial'
                      ? 'Thanh toán một phần'
                      : s,
          }));

          return (
            <Card
              style={{ marginTop: 16 }}
              title={`Chi tiết hóa đơn - Nhóm: ${factoryGroupDetail.group}`}
              extra={
                <Button
                  type="link"
                  onClick={() => {
                    setFactoryGroupDetail(null);
                    setFactoryGroupInvoiceType('all');
                    setFactoryGroupInvoiceStatus('all');
                  }}
                >
                  Đóng
                </Button>
              }
            >
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={6}>
                  <Card bordered={false} style={{ background: '#f6ffed' }}>
                    <div style={{ fontSize: 12, color: '#389e0d' }}>Tổng doanh thu nhóm</div>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>
                      {totalAmount.toLocaleString('vi-VN')} VNĐ
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card bordered={false} style={{ background: '#e6f7ff' }}>
                    <div style={{ fontSize: 12, color: '#1890ff' }}>Đã thu</div>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>
                      {paidAmount.toLocaleString('vi-VN')} VNĐ
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card bordered={false} style={{ background: '#fff7e6' }}>
                    <div style={{ fontSize: 12, color: '#fa8c16' }}>Còn nợ</div>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>
                      {remainingAmount.toLocaleString('vi-VN')} VNĐ
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card bordered={false} style={{ background: '#fff1f0' }}>
                    <div style={{ fontSize: 12, color: '#f5222d' }}>Quá hạn</div>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>
                      {overdueAmount.toLocaleString('vi-VN')} VNĐ
                    </div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      Tỷ lệ thu: {paymentRate.toFixed(2)}%
                    </div>
                  </Card>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={6}>
                  <Select
                    allowClear
                    value={factoryGroupInvoiceType === 'all' ? undefined : factoryGroupInvoiceType}
                    onChange={value => setFactoryGroupInvoiceType(value || 'all')}
                    placeholder="Lọc theo loại hóa đơn"
                    style={{ width: '100%' }}
                    options={typeOptions}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Select
                    allowClear
                    value={
                      factoryGroupInvoiceStatus === 'all'
                        ? undefined
                        : factoryGroupInvoiceStatus
                    }
                    onChange={value => setFactoryGroupInvoiceStatus(value || 'all')}
                    placeholder="Lọc theo trạng thái"
                    style={{ width: '100%' }}
                    options={statusOptions}
                  />
                </Col>
              </Row>

              <Table
                dataSource={filteredInvoices}
                rowKey={(row, index) => row.InvoiceID || row.BillID || index}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
                columns={[
                  {
                    title: 'Mã hóa đơn',
                    dataIndex: 'InvoiceID',
                    key: 'InvoiceID',
                    render: (value, row) => value || row.BillID || '-',
                  },
                  {
                    title: 'Loại',
                    dataIndex: 'Type',
                    key: 'Type',
                    render: type => {
                      const map = {
                        rent: 'Tiền thuê',
                        electricity: 'Tiền điện',
                        water: 'Tiền nước sạch',
                        service: 'Dịch vụ',
                      };
                      return map[type] || type || '-';
                    },
                  },
                  {
                    title: 'Nhà xưởng',
                    dataIndex: 'FactoryName',
                    key: 'FactoryName',
                  },
                  {
                    title: 'Khách thuê',
                    dataIndex: 'TenantName',
                    key: 'TenantName',
                  },
                  {
                    title: 'Số tiền (VNĐ)',
                    dataIndex: 'Amount',
                    key: 'Amount',
                    align: 'right',
                    render: value => new Intl.NumberFormat('vi-VN').format(value || 0),
                  },
                  {
                    title: 'Đã thanh toán (VNĐ)',
                    dataIndex: 'PaidAmount',
                    key: 'PaidAmount',
                    align: 'right',
                    render: value => new Intl.NumberFormat('vi-VN').format(value || 0),
                  },
                  {
                    title: 'Trạng thái',
                    dataIndex: 'Status',
                    key: 'Status',
                    render: status => {
                      const color =
                        status === 'paid'
                          ? 'green'
                          : status === 'overdue'
                            ? 'red'
                            : status === 'partial'
                              ? 'orange'
                              : 'default';
                      const label =
                        status === 'paid'
                          ? 'Đã thanh toán'
                          : status === 'overdue'
                            ? 'Quá hạn'
                            : status === 'pending' || status === 'unpaid'
                              ? 'Chưa thanh toán'
                              : status === 'partial'
                                ? 'Thanh toán một phần'
                                : status || '-';
                      return <Tag color={color}>{label}</Tag>;
                    },
                  },
                  {
                    title: 'Ngày hóa đơn',
                    dataIndex: 'BillDate',
                    key: 'BillDate',
                    render: date =>
                      date ? (date.split('T')[0] || '').split('-').reverse().join('/') : '-',
                  },
                ]}
              />
            </Card>
          );
        })()}
      </Card>
    );
  };

  const items = [
    {
      key: 'general',
      label: 'Tổng quan',
      children: renderGeneralTab(),
    },
    {
      key: 'tenant',
      label: 'Theo khách thuê',
      children: renderTenantTab(),
    },
    {
      key: 'invoice',
      label: 'Xem hóa đơn',
      children: renderInvoiceTab(),
    },
    {
      key: 'monthly',
      label: 'So sánh theo tháng',
      children: renderMonthlyComparisonTab(),
    },
    {
      key: 'factoryGroup',
      label: 'Theo nhóm nhà xưởng',
      children: renderFactoryGroupTab(),
    },
  ];

  return (
    <App>
      <div>
        <Card
          title={<h2 style={{ margin: 0 }}>Báo Cáo Thống Kê</h2>}
          bordered={false}
          loading={loading}
          bodyStyle={{ padding: isMobile ? 8 : 16 }}
        >
          <Tabs
            defaultActiveKey="1"
            activeKey={activeTab}
            onChange={handleTabChange}
            items={items}
          />
        </Card>
      </div>
      <InvoiceDetailModal
        visible={detailModal.visible}
        record={detailModal.record}
        onCancel={() => setDetailModal({ visible: false, record: null })}
      />
    </App>
  );
}

export default Reports; 